/**
 * Ticket System - Shared Utilities
 * 
 * PC側（Electron）とモバイル側（React Native）で共有する処理
 */

import {
  initializeApp,
  getApp,
  getApps
} from 'firebase/app';
import {
  getAuth,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signInWithCredential,
  linkWithPopup,
  GoogleAuthProvider,
  OAuthProvider,
  signOut,
  onAuthStateChanged,
  signInAnonymously
} from 'firebase/auth';
import {
  getFunctions,
  httpsCallable
} from 'firebase/functions';
import {
  getFirestore,
  collection,
  doc,
  setDoc,
  getDoc,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  writeBatch,
  serverTimestamp as firestoreServerTimestamp,
  increment as firestoreIncrement,
  arrayUnion,
  FieldValue
} from 'firebase/firestore';
// Realtime Database（tickets用）
import {
  getDatabase,
  ref,
  set,
  get,
  update,
  remove,
  push,
  query as rtdbQuery,
  orderByChild,
  equalTo,
  serverTimestamp as rtdbServerTimestamp,
  increment as rtdbIncrement,
  onValue,
  onChildAdded,
  onChildChanged,
  onChildRemoved,
  off
} from 'firebase/database';
import { firebaseConfigTickets, firestoreDatabaseId, realtimeDatabaseEnvPath } from '../../../shared/firebaseConfig_tickets';
import {
  getTicketsPath,
  getQRChunksPath,
  getQRIndexPath,
  getQRCodesPath,
  getPermissionsPath,
  getUserCanonicalLinksPath,
  convertFirestoreToRtdb,
  convertRtdbToFirestore,
  snapshotToArray,
  createMultiPathUpdates,
  encodeQRCode,
  decodeQRCode
} from '../../../shared/ticketRtdbHelpers';

// ========================================
// Firebase 初期化（シングルトン）
// ========================================

let firebaseApp = null;
let firebaseAuth = null;
let firestoreDb = null;
let realtimeDb = null;

export const initFirebaseTickets = () => {
  // 既に初期化済みならそれを返す
  if (getApps().length > 0) {
    firebaseApp = getApp();
  } else {
    // 実行時にauthDomainを設定
    const config = {
      ...firebaseConfigTickets,
      authDomain: typeof window !== 'undefined' ? window.location.hostname : firebaseConfigTickets.authDomain
    };
    console.log('[initFirebaseTickets] Initializing with authDomain:', config.authDomain);
    firebaseApp = initializeApp(config);
  }
  
  firebaseAuth = getAuth(firebaseApp);
  firestoreDb = getFirestore(firebaseApp, firestoreDatabaseId);
  realtimeDb = getDatabase(firebaseApp);
  
  return {
    app: firebaseApp,
    auth: firebaseAuth,
    db: firestoreDb,  // Firestore（users, subscriptions用）
    rtdb: realtimeDb  // Realtime Database（tickets用）
  };
};

export const getFirebaseTickets = () => {
  // 環境に応じたRealtime Databaseパスを使用（firebaseConfig_ticketsから取得）
  return {
    app: firebaseApp,
    auth: firebaseAuth,
    db: firestoreDb,
    rtdb: realtimeDb,
    realtimeDatabasePath: realtimeDatabaseEnvPath
  };
};

export const observeAuthState = (callback) => {
  const { auth } = initFirebaseTickets();
  if (!auth || typeof callback !== 'function') {
    return () => {};
  }

  return onAuthStateChanged(auth, callback);
};

// ========================================
// ユーザー認証関連
// ========================================

/**
 * デバイスUnique ID生成（モバイル側）
 * 初回起動時のみ生成、LocalStorageに保存
 */
export const generateDeviceUniqueId = () => {
  const stored = typeof localStorage !== 'undefined' ? localStorage.getItem('deviceUniqueId') : null;
  if (stored) return stored;
  
  const newId = `device_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem('deviceUniqueId', newId);
  }
  return newId;
};

/**
 * ユーザーUnique ID生成（PC側 - 初回起動時）
 */
export const generateUserUniqueId = () => {
  const newId = `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  return newId;
};

/**
 * 20文字のチケットIDを生成（大文字限定、判定時は大文字小文字区別しない）
 * 例: ABCDEFGHIJKLMNOPQRST
 */
export const generateTicketId = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let id = '';
  for (let i = 0; i < 20; i++) {
    id += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return id;
};

/**
 * Snap環境を検出
 */
const isSnapEnvironment = () => {
  if (typeof process === 'undefined') return false;
  return !!(process.env.SNAP || process.env.SNAP_NAME);
};

/**
 * Google ログイン
 * Snap環境では signInWithPopup、それ以外のElectronでは signInWithRedirect、Webでは signInWithPopup を使用
 */
export const loginWithGoogle = async (isElectron = false) => {
  try {
    const { auth } = initFirebaseTickets();
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({
      prompt: 'select_account'
    });
    
    const isSnap = isSnapEnvironment();
    
    if (isElectron && !isSnap) {
      // Electron (非Snap): リダイレクト方式
      console.log('[loginWithGoogle] Starting redirect auth (Electron)...');
      await signInWithRedirect(auth, provider);
      return null;
    } else {
      // Snap環境 または Web: ポップアップ方式
      console.log(`[loginWithGoogle] Starting popup auth (${isSnap ? 'Snap' : 'Web'})...`);

      // 匿名ユーザーがいる場合はアカウント統合（Firebase UIDを保持することでゲストチケットを引き継ぎ）
      let result;
      if (auth.currentUser?.isAnonymous) {
        try {
          console.log('[loginWithGoogle] Anonymous user detected, trying linkWithPopup...');
          result = await linkWithPopup(auth.currentUser, provider);
          console.log('[loginWithGoogle] Anonymous account linked successfully');
        } catch (linkError) {
          // 既存のGoogleアカウントがある場合などは通常のサインインにフォールバック
          console.warn('[loginWithGoogle] linkWithPopup failed, falling back to signInWithPopup:', linkError.code);
          result = await signInWithPopup(auth, provider);
        }
      } else {
        result = await signInWithPopup(auth, provider);
      }

      const user = result.user;

      await saveUserProfile({
        uid: user.uid,
        displayName: user.displayName,
        email: user.email,
        authProvider: 'google'
      });

      return user;
    }
  } catch (error) {
    console.error('Google login failed:', error);
    throw error;
  }
};

/**
 * リダイレクト認証の結果を処理
 */
export const handleRedirectResult = async () => {
  try {
    const { auth } = initFirebaseTickets();
    const result = await getRedirectResult(auth);
    
    if (result && result.user) {
      console.log('[handleRedirectResult] User logged in via redirect:', result.user.email);
      
      await saveUserProfile({
        uid: result.user.uid,
        displayName: result.user.displayName,
        email: result.user.email,
        authProvider: 'google'
      });
      
      return result.user;
    }
    
    return null;
  } catch (error) {
    console.error('[handleRedirectResult] Error:', error);
    throw error;
  }
};

/**
 * LINE ログイン（PC側 - Electron）
 */
export const loginWithLine = async () => {
  try {
    const { auth } = initFirebaseTickets();
    const provider = new OAuthProvider('oidc.oidc.line');

    // 匿名ユーザーがいる場合はアカウント統合（Firebase UIDを保持することでゲストチケットを引き継ぎ）
    let result;
    if (auth.currentUser?.isAnonymous) {
      try {
        console.log('[loginWithLine] Anonymous user detected, trying linkWithPopup...');
        result = await linkWithPopup(auth.currentUser, provider);
        console.log('[loginWithLine] Anonymous account linked successfully');
      } catch (linkError) {
        console.warn('[loginWithLine] linkWithPopup failed, falling back to signInWithPopup:', linkError.code);
        result = await signInWithPopup(auth, provider);
      }
    } else {
      result = await signInWithPopup(auth, provider);
    }

    const user = result.user;

    await saveUserProfile({
      uid: user.uid,
      displayName: user.displayName,
      email: user.email,
      authProvider: 'line'
    });

    return user;
  } catch (error) {
    console.error('LINE login failed:', error);
    throw error;
  }
};

/**
 * 認証なしでログイン（ユニークID使用）
 * Firebase Anonymous Auth が有効になっていない場合は、ローカル認証を使用
 */
export const loginAnonymously = async (userUniqueId) => {
  try {
    const { auth } = initFirebaseTickets();
    let firebaseUid = null;
    let authErrorCode = null;

    try {
      const anonymousResult = await signInAnonymously(auth);
      firebaseUid = anonymousResult?.user?.uid || null;
    } catch (anonymousError) {
      console.warn('Anonymous auth unavailable, fallback to local session:', anonymousError);
      authErrorCode = anonymousError?.code || 'anonymous-auth-failed';
    }
    
    // ローカルストレージに保存（Firebase Auth を使わない簡易認証）
    localStorage.setItem('userUniqueId', userUniqueId);
    localStorage.setItem('authProvider', 'local');
    localStorage.setItem('lastLogin', new Date().toISOString());
    localStorage.setItem('firebaseAuthReady', firebaseUid ? 'true' : 'false');
    if (firebaseUid) {
      localStorage.setItem('firebaseUid', firebaseUid);
      localStorage.removeItem('firebaseAuthErrorCode');
    } else if (authErrorCode) {
      localStorage.setItem('firebaseAuthErrorCode', authErrorCode);
    }
    
    // Firestore に保存（認証なし）
    try {
      await saveUserProfile({
        uid: userUniqueId,
        displayName: `ユーザー ${userUniqueId.slice(0, 8)}`,
        email: null,
        authProvider: 'local',
        firebaseUid,
        createdAt: new Date(),
        lastLogin: new Date()
      });
    } catch (profileError) {
      console.warn('Save profile skipped for local login:', profileError);
    }
    
    return {
      uid: userUniqueId,
      firebaseUid,
      isFirebaseAuthenticated: Boolean(firebaseUid),
      authErrorCode
    };
  } catch (error) {
    console.warn('Anonymous login fallback mode:', error);
    return {
      uid: userUniqueId,
      firebaseUid: null,
      isFirebaseAuthenticated: false,
      authErrorCode: error?.code || 'anonymous-login-error'
    };
  }
};

/**
 * ゲストユーザーのアカウントをGoogle/LINEプロバイダーに連携する
 * 既に連携済みの場合は上書きして wasAlreadyLinked: true を返す
 */
export const linkAnonymousWithProvider = async (provider, canonicalUserId, confirmIfAlreadyLinked) => {
  const { auth, db } = initFirebaseTickets();

  let providerResult;
  if (provider === 'google') {
    const googleProvider = new GoogleAuthProvider();
    googleProvider.setCustomParameters({ prompt: 'select_account' });
    providerResult = await signInWithPopup(auth, googleProvider);
  } else if (provider === 'line') {
    const lineProvider = new OAuthProvider('oidc.oidc.line');
    providerResult = await signInWithPopup(auth, lineProvider);
  } else {
    throw new Error('Unsupported provider: ' + provider);
  }

  const user = providerResult.user;
  const providerEmail = (user.email || user.uid).trim().toLowerCase();
  const firebaseUid = user.uid;
  const displayName = user.displayName;

  // 既存リンクを確認（上書き連携のため存在チェックのみ）
  // 初回連携時はドキュメントが存在しないため permission-denied になる場合があるので try-catch する
  const linkDocId = `${provider}:${providerEmail}`;
  const linkRef = doc(db, 'auth_identity_links', linkDocId);
  let wasAlreadyLinked = false;
  let existingLinkedAt = null;
  let existingCanonicalUserId = null;
  try {
    const linkSnap = await getDoc(linkRef);
    wasAlreadyLinked = linkSnap.exists();
    if (wasAlreadyLinked) {
      existingLinkedAt = linkSnap.data().linkedAt;
      existingCanonicalUserId = linkSnap.data().canonicalUserId || null;
    }
  } catch (readError) {
    const code = readError?.code || '';
    if (code !== 'permission-denied' && code !== 'unauthenticated') throw readError;
    // permission-denied = ドキュメント未存在 → 初回連携として扱う
  }

  // 連携済みの場合は確認コールバックを呼ぶ（NOなら中断）
  if (wasAlreadyLinked && confirmIfAlreadyLinked) {
    const confirmed = await confirmIfAlreadyLinked();
    if (!confirmed) return { cancelled: true };
  }

  // 連携済みの場合は既存の canonicalUserId を優先（連携先のデータを引き継ぐ）
  const effectiveCanonicalUserId = (wasAlreadyLinked && existingCanonicalUserId)
    ? existingCanonicalUserId
    : canonicalUserId;

  // リンクを作成/更新
  const timestamp = firestoreServerTimestamp();
  await setDoc(linkRef, {
    provider,
    providerEmail,
    firebaseUid,
    canonicalUserId: effectiveCanonicalUserId,
    displayName: displayName || null,
    linkedAt: existingLinkedAt || timestamp,
    lastLoginAt: timestamp
  }, { merge: true });

  // プロフィール更新
  const profileUpdates = {
    uid: effectiveCanonicalUserId,
    authProvider: provider,
    displayName: displayName || null,
    email: providerEmail,
    firebaseUid
  };
  if (provider === 'google') {
    profileUpdates.googleEmail = providerEmail;
    profileUpdates.googleDisplayName = displayName;
  }
  await saveUserProfile(profileUpdates);

  return { uid: effectiveCanonicalUserId, providerEmail, displayName, firebaseUid, provider, wasAlreadyLinked, cancelled: false };
};

/**
 * ユーザープロフィール保存
 */
export const saveUserProfile = async (profileData) => {
  try {
    const { db } = initFirebaseTickets();
    
    // canonicalUserIdをドキュメントIDとして使用
    // initialUniqueIdフィールドは不要（ドキュメントIDと重複）
    const timestamp = firestoreServerTimestamp();
    await setDoc(doc(db, 'users', profileData.uid), {
      displayName: profileData.displayName || null,
      email: profileData.email || null,
      authProvider: profileData.authProvider || 'local',
      firebaseUid: profileData.firebaseUid || null,
      googleEmail: profileData.googleEmail || null,
      googleDisplayName: profileData.googleDisplayName || null,
      createdAt: profileData.createdAt || timestamp,
      updatedAt: timestamp,
      lastLoginAt: timestamp,
      deviceUniqueId: generateDeviceUniqueId()
    }, { merge: true });
    console.log('[saveUserProfile] Profile saved for uid:', profileData.uid);
    return true;
  } catch (error) {
    const code = error?.code || '';
    if (
      code === 'permission-denied' ||
      code === 'unauthenticated' ||
      code === 'failed-precondition'
    ) {
      console.warn('Save profile skipped (permission):', error);
      return false;
    }

    console.warn('Save profile skipped:', error);
    return false;
  }
};

/**
 * createdBy を古いIDから新しいIDへ移行
 */
/**
 * canonical ユーザーID と Firebase UID のマッピングを RTDB に保存
 * ゲスト→別アカウント移行時にチケット所有権を引き継ぐために使用
 * RTDB ルール: $firebaseUid == auth.uid で自分の UID のみ書き込み可能
 */
export const saveUserCanonicalLink = async (canonicalUserId, firebaseUid) => {
  if (!canonicalUserId || !firebaseUid) return;
  try {
    const { rtdb } = initFirebaseTickets();
    const linkRef = ref(rtdb, getUserCanonicalLinksPath(canonicalUserId, firebaseUid));
    await set(linkRef, true);
    console.log('[saveUserCanonicalLink] Saved link:', canonicalUserId, '->', firebaseUid);
  } catch (error) {
    // 権限エラーは静かに無視（ルールが未デプロイの場合など）
    console.warn('[saveUserCanonicalLink] Failed (ignoring):', error.code || error.message);
  }
};

export const migrateTicketsCreatedBy = async (fromUserIds, toUserId, newFirebaseUid = null) => {
  const normalizedToUserId = (toUserId || '').trim();
  if (!normalizedToUserId) {
    return { migratedCount: 0 };
  }

  const safeFromUserIds = Array.from(new Set((fromUserIds || [])
    .map((id) => (id || '').trim())
    .filter((id) => id && id !== normalizedToUserId)));

  let migratedCount = 0;

  for (const fromUserId of safeFromUserIds) {
    let tickets = [];
    try {
      tickets = await getUserTickets(fromUserId);
    } catch (error) {
      console.warn('migrateTicketsCreatedBy: Failed to read tickets for', fromUserId, error);
      continue;
    }

    for (const ticket of tickets) {
      if (!ticket?.id) continue;

      try {
        const updateData = { createdBy: normalizedToUserId };
        // createdByFirebaseUidが未設定（ゲスト作成）の場合は新しいFirebase UIDで更新
        if (newFirebaseUid && !ticket.createdByFirebaseUid) {
          updateData.createdByFirebaseUid = newFirebaseUid;
        }
        await updateTicket(ticket.id, updateData);
        migratedCount += 1;
      } catch (updateError) {
        console.warn('migrateTicketsCreatedBy: Failed to update ticket', ticket.id, updateError);
      }
    }
  }

  return { migratedCount };
};

/**
 * ユーザープロフィール取得
 */
export const getUserProfile = async (userId) => {
  try {
    if (!userId || typeof userId !== 'string' || !userId.trim()) {
      return null;
    }

    const { db } = initFirebaseTickets();
    const normalizedUserId = userId.trim();
    const docRef = doc(db, 'users', normalizedUserId);
    const docSnap = await getDoc(docRef);
    
    return docSnap.exists() ? docSnap.data() : null;
  } catch (error) {
    const code = error?.code || '';
    if (
      code === 'permission-denied' ||
      code === 'unauthenticated' ||
      code === 'failed-precondition'
    ) {
      return null;
    }

    console.warn('Get profile skipped:', error);
    return null;
  }
};

/**
 * ログアウト
 */
export const logoutUser = async () => {
  try {
    const { auth } = initFirebaseTickets();
    await signOut(auth);
  } catch (error) {
    console.error('Logout failed:', error);
    throw error;
  }
};

// ========================================
// チケット操作
// ========================================

const LOCAL_TICKETS_KEY = 'localTickets';
const LOCAL_QR_CODES_PREFIX = 'localQRCodes:';
const LOCAL_SUBSCRIPTIONS_KEY = 'localSubscriptions';
const QR_CHUNKS_COLLECTION = 'qr_chunks';
const QR_INDEX_COLLECTION = 'qr_index';
const QR_CHUNK_SIZE = 500;
const FUNCTIONS_REGION = 'asia-northeast1';

// Product Masterから商品情報を取得
const PRODUCT_FALLBACK_CONFIG = {
  'QR01': {
    id: 'QR01',
    price: 100,
    qrCount: 10,
    durationDays: 30,
    productType: 'one_time'
  },
  'QR10': {
    id: 'QR10',
    price: 300,
    qrCount: 10,
    durationDays: 30,
    productType: 'one_time'
  }
};

const getProductFromMaster = async (productId) => {
  try {
    const { db } = initFirebaseTickets();
    const productDoc = await getDoc(doc(db, 'product_master', productId));
    
    if (!productDoc.exists()) {
      console.error(`[getProductFromMaster] Product not found: ${productId}`);
      // フォールバック設定を使用
      const fallback = PRODUCT_FALLBACK_CONFIG[productId];
      if (fallback) {
        console.warn(`[getProductFromMaster] Using fallback config for: ${productId}`);
        return fallback;
      }
      return null;
    }
    
    const data = productDoc.data();
    if (data.deleted) {
      console.error(`[getProductFromMaster] Product is deleted: ${productId}`);
      return null;
    }
    
    return {
      id: productDoc.id,
      price: data.unitAmount,
      qrCount: data.slots,
      durationDays: data.validDays,
      productType: data.productType
    };
  } catch (error) {
    console.error('[getProductFromMaster] Error:', error);
    // エラー時もフォールバック設定を使用
    const fallback = PRODUCT_FALLBACK_CONFIG[productId];
    if (fallback) {
      console.warn(`[getProductFromMaster] Using fallback config due to error: ${productId}`);
      return fallback;
    }
    return null;
  }
};

const COMMUNICATION_ERROR_CODES = new Set([
  'unavailable',
  'deadline-exceeded',
  'aborted',
  'cancelled',
  'network-request-failed'
]);

const normalizeErrorCode = (error) => {
  const raw = String(error?.code || '').trim();
  if (!raw) return '';
  return raw.includes('/') ? raw.split('/').pop() : raw;
};

const isCommunicationError = (error) => COMMUNICATION_ERROR_CODES.has(normalizeErrorCode(error));
const isPermissionLikeError = (error) => {
  const code = normalizeErrorCode(error);
  return code === 'permission-denied' || code === 'unauthenticated' || code === 'failed-precondition';
};

export const readLocalTickets = () => {
  if (typeof localStorage === 'undefined') return [];
  try {
    const raw = localStorage.getItem(LOCAL_TICKETS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const isQuotaExceededError = (e) =>
  e instanceof DOMException && (e.code === 22 || e.code === 1014 || e.name === 'QuotaExceededError');

const showStorageQuotaAlert = () => {
  try {
    const { getTranslation } = require('./i18n');
    const lang = (typeof localStorage !== 'undefined' && localStorage.getItem('appLanguage')) || 'en';
    const msg = getTranslation(lang, 'storageQuotaExceeded');
    if (typeof window !== 'undefined' && window.alert) {
      window.alert(msg);
    }
  } catch {
    // フォールバック
    if (typeof window !== 'undefined' && window.alert) {
      window.alert('Local storage capacity exceeded.');
    }
  }
};

const writeLocalTickets = (tickets) => {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(LOCAL_TICKETS_KEY, JSON.stringify(tickets));
  } catch (e) {
    if (isQuotaExceededError(e)) showStorageQuotaAlert();
  }
};

const readLocalSubscriptions = () => {
  if (typeof localStorage === 'undefined') return [];
  try {
    const raw = localStorage.getItem(LOCAL_SUBSCRIPTIONS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeLocalSubscriptions = (subscriptions) => {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(LOCAL_SUBSCRIPTIONS_KEY, JSON.stringify(subscriptions));
  } catch (e) {
    if (isQuotaExceededError(e)) showStorageQuotaAlert();
  }
};

const writeLocalQRCodes = (ticketId, qrCodes) => {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(`${LOCAL_QR_CODES_PREFIX}${ticketId}`, JSON.stringify(qrCodes));
  } catch (e) {
    if (isQuotaExceededError(e)) showStorageQuotaAlert();
  }
};

const readLocalQRCodes = (ticketId) => {
  if (typeof localStorage === 'undefined') return [];
  try {
    const raw = localStorage.getItem(`${LOCAL_QR_CODES_PREFIX}${ticketId}`);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const normalizeDateLike = (value) => {
  if (!value) return null;
  if (typeof value?.toDate === 'function') {
    return value.toDate().toISOString();
  }
  if (value instanceof Date) {
    return value.toISOString();
  }
  return value;
};

const createChunkId = (offsetIndex) => `chunk_${String(offsetIndex).padStart(6, '0')}`;
const createQRIndexDocId = (code) => encodeURIComponent(String(code || ''));

const splitArrayBySize = (items, size) => {
  const chunks = [];
  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size));
  }
  return chunks;
};

const createDeleteAtAfterOneYear = (baseDate = new Date()) => {
  const deleteAt = new Date(baseDate);
  deleteAt.setFullYear(deleteAt.getFullYear() + 1);
  return deleteAt;
};

export const clearLocalRuntimeData = () => {
  if (typeof localStorage === 'undefined') return;

  try {
    localStorage.removeItem(LOCAL_TICKETS_KEY);
    localStorage.removeItem(LOCAL_SUBSCRIPTIONS_KEY);

    const keysToDelete = [];
    for (let index = 0; index < localStorage.length; index += 1) {
      const key = localStorage.key(index);
      if (key && key.startsWith(LOCAL_QR_CODES_PREFIX)) {
        keysToDelete.push(key);
      }
    }

    keysToDelete.forEach((key) => localStorage.removeItem(key));
  } catch {
    // no-op
  }
};

const deleteDocsByQuery = async (db, q) => {
  let deletedCount = 0;

  while (true) {
    const snapshot = await getDocs(q);
    if (snapshot.empty) break;

    const batch = writeBatch(db);
    snapshot.docs.forEach((docSnap) => {
      batch.delete(docSnap.ref);
      deletedCount += 1;
    });
    await batch.commit();

    if (snapshot.size < 500) break;
  }

  return deletedCount;
};

export const deleteAllUserData = async (userId) => {
  const normalizedUserId = (userId || '').trim();
  if (!normalizedUserId) {
    return {
      deletedTickets: 0,
      deletedSubscriptions: 0,
      deletedProfiles: 0,
      deletedIdentityLinks: 0,
      failedOperations: [],
      deletedLocalData: true
    };
  }

  let deletedTickets = 0;
  let deletedSubscriptions = 0;
  let deletedProfiles = 0;
  let deletedIdentityLinks = 0;
  const failedOperations = [];

  try {
    const { db, rtdb, auth } = initFirebaseTickets();
    const firebaseUid = auth?.currentUser?.uid || null;

    // RTDB: Delete all tickets created by this user
    try {
      const ticketsRef = ref(rtdb, getTicketsPath());
      
      // Query by createdBy (user unique ID)
      const q1 = rtdbQuery(ticketsRef, orderByChild('createdBy'), equalTo(normalizedUserId));
      const snapshot1 = await get(q1);
      
      if (snapshot1.exists()) {
        const tickets = snapshotToArray(snapshot1);
        console.log(`[deleteAllUserData] Found ${tickets.length} tickets by createdBy`);
        for (const ticket of tickets) {
          const deleted = await deleteTicket(ticket.id);
          if (deleted) {
            deletedTickets += 1;
          } else {
            failedOperations.push(`ticket:${ticket.id}`);
          }
        }
      }
      
      // Also query by createdByFirebaseUid if available
      if (firebaseUid) {
        const q2 = rtdbQuery(ticketsRef, orderByChild('createdByFirebaseUid'), equalTo(firebaseUid));
        const snapshot2 = await get(q2);
        
        if (snapshot2.exists()) {
          const tickets = snapshotToArray(snapshot2);
          console.log(`[deleteAllUserData] Found ${tickets.length} tickets by createdByFirebaseUid`);
          for (const ticket of tickets) {
            // Skip if already deleted
            const alreadyDeleted = deletedTickets > 0;
            if (!alreadyDeleted) {
              const deleted = await deleteTicket(ticket.id);
              if (deleted) {
                deletedTickets += 1;
              } else {
                failedOperations.push(`ticket:${ticket.id}`);
              }
            }
          }
        }
      }
      
      console.log(`[deleteAllUserData] Total deleted tickets: ${deletedTickets}`);
    } catch (error) {
      failedOperations.push('tickets_query');
      if (!isPermissionLikeError(error)) {
        console.warn('deleteAllUserData: tickets delete failed:', error);
      }
    }

    // Firestore: Delete subscriptions
    try {
      deletedSubscriptions = await deleteDocsByQuery(
        db,
        query(collection(db, 'subscriptions'), where('userId', '==', normalizedUserId))
      );
      console.log(`[deleteAllUserData] Deleted ${deletedSubscriptions} subscriptions`);
    } catch (error) {
      failedOperations.push('subscriptions');
      if (!isPermissionLikeError(error)) {
        console.warn('deleteAllUserData: subscriptions delete failed:', error);
      }
    }

    // Firestore: Delete auth_identity_links
    try {
      deletedIdentityLinks = await deleteDocsByQuery(
        db,
        query(collection(db, 'auth_identity_links'), where('canonicalUserId', '==', normalizedUserId))
      );
      console.log(`[deleteAllUserData] Deleted ${deletedIdentityLinks} identity links`);
    } catch (error) {
      failedOperations.push('auth_identity_links');
      if (!isPermissionLikeError(error)) {
        console.warn('deleteAllUserData: identity links delete failed:', error);
      }
    }

    // Firestore: Delete user profile
    try {
      const profileRef = doc(db, 'users', normalizedUserId);
      const profileSnapshot = await getDoc(profileRef);
      if (profileSnapshot.exists()) {
        await deleteDoc(profileRef);
        deletedProfiles = 1;
        console.log(`[deleteAllUserData] Deleted user profile`);
      }
    } catch (error) {
      failedOperations.push('user_profile');
      if (!isPermissionLikeError(error)) {
        console.warn('deleteAllUserData: profile delete failed:', error);
      }
    }
  } finally {
    clearLocalRuntimeData();
    console.log(`[deleteAllUserData] Summary: ${deletedTickets} tickets, ${deletedSubscriptions} subscriptions, ${deletedProfiles} profiles, ${deletedIdentityLinks} identity links`);
  }

  return {
    deletedTickets,
    deletedSubscriptions,
    deletedProfiles,
    deletedIdentityLinks,
    failedOperations,
    deletedLocalData: true
  };
};

/**
 * チケット作成
 */
export const createTicket = async (ticketData) => {
  try {
    const { rtdb, auth } = initFirebaseTickets();
    const authUid = auth?.currentUser?.uid || null;
    const authEmail = auth?.currentUser?.email || null;
    const now = new Date();
    const deleteAt = createDeleteAtAfterOneYear(now);
    const normalizedTicketData = {
      ...ticketData,
      plan: ticketData.plan || 'starter',
      maxQRCodes: Number(ticketData.maxQRCodes || 100),
      usedQRCodes: Number(ticketData.usedQRCodes || 0),
      createdByFirebaseUid: ticketData.createdByFirebaseUid || authUid,
      createdByEmail: ticketData.createdByEmail || authEmail,
      deleteAt: ticketData.deleteAt || deleteAt
    };
    
    // 独自のチケットIDを生成（20文字の英数字）大文字限定　判定時は大文字小文字区別しない
    const ticketId = generateTicketId();

    const ticketsRef = ref(rtdb, getTicketsPath());
    const newTicketRef = ref(rtdb, `${getTicketsPath()}/${ticketId}`);
    
    const data = convertFirestoreToRtdb({
      ...normalizedTicketData,
      createdAt: rtdbServerTimestamp(),
      status: 'active',
      usedQRCodes: normalizedTicketData.usedQRCodes
    });
    
    console.log('[createTicket] Creating ticket in RTDB:', ticketId, data);
    await set(newTicketRef, data);
    console.log('[createTicket] Ticket created successfully');

    // リアルタイムリスナーが自動的に更新するため、ローカルストレージへの書き込みは不要
    
    return ticketId;
  } catch (error) {
    if (isCommunicationError(error)) {
      const retryError = new Error('通信エラーが発生しました。時間をおいて再試行してください。');
      retryError.code = normalizeErrorCode(error) || 'network-request-failed';
      throw retryError;
    }

    console.warn('Create ticket fallback to local:', error);

    const localId = `local_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const localTicket = {
      id: localId,
      ...ticketData,
      status: 'active',
      usedQRCodes: 0,
      createdAt: new Date().toISOString(),
      deleteAt: createDeleteAtAfterOneYear().toISOString()
    };

    const localTickets = readLocalTickets();
    localTickets.push(localTicket);
    writeLocalTickets(localTickets);

    return localId;
  }
};

/**
 * チケット取得
 */
export const getTicket = async (ticketId) => {
  try {
    const { rtdb } = initFirebaseTickets();
    const ticketRef = ref(rtdb, getTicketsPath(ticketId));
    const snapshot = await get(ticketRef);
    
    if (!snapshot.exists()) return null;
    
    const data = convertRtdbToFirestore(snapshot.val());
    return { id: ticketId, ...data };
  } catch (error) {
    console.error('Get ticket failed:', error);
    throw error;
  }
};

/**
 * チケット情報をリアルタイム監視
 * @param {string} ticketId - チケットID
 * @param {function} callback - 更新時のコールバック関数
 * @returns {function} - unsubscribe関数
 */
export const subscribeToTicket = (ticketId, callback) => {
  try {
    const { rtdb } = initFirebaseTickets();
    const ticketRef = ref(rtdb, getTicketsPath(ticketId));
    
    const unsubscribe = onValue(ticketRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = convertRtdbToFirestore(snapshot.val());
        callback({ id: ticketId, ...data });
      } else {
        callback(null);
      }
    }, (error) => {
      console.error('Subscribe to ticket failed:', error);
      callback(null);
    });
    
    return unsubscribe;
  } catch (error) {
    console.error('Subscribe to ticket setup failed:', error);
    return () => {}; // no-op unsubscribe
  }
};

/**
 * QR入場状態をリアルタイム監視
 * @param {string} ticketId - チケットID
 * @param {function} callback - 更新時のコールバック関数 (event) => void
 * @returns {function} - unsubscribe関数
 */
export const subscribeToQREntries = (ticketId, callback) => {
  try {
    const { rtdb } = initFirebaseTickets();
    const indexRef = ref(rtdb, getQRIndexPath(ticketId));
    
    const unsubscribes = [];
    
    // Helper function to fetch freeText from qr_chunks
    const fetchFreeText = async (entry) => {
      if (entry.chunkId != null && entry.offsetInChunk != null) {
        try {
          const chunkRef = ref(rtdb, `${getTicketsPath(ticketId)}/qr_chunks/${entry.chunkId}`);
          const chunkSnap = await get(chunkRef);
          
          if (chunkSnap.exists()) {
            const chunk = chunkSnap.val();
            const qrData = chunk.codes?.[entry.offsetInChunk];
            
            if (qrData && qrData.freeText) {
              return qrData.freeText;
            }
          }
        } catch (error) {
          console.warn('[subscribeToQREntries] Failed to fetch freeText:', error);
        }
      }
      return '';
    };
    
    // 新しい入場
    const onAddedUnsubscribe = onChildAdded(indexRef, async (snapshot) => {
      const entry = convertRtdbToFirestore(snapshot.val());
      const freeText = await fetchFreeText(entry);
      callback({
        type: 'added',
        id: snapshot.key,
        ...entry,
        freeText
      });
    });
    unsubscribes.push(onAddedUnsubscribe);
    
    // 入場情報の更新
    const onChangedUnsubscribe = onChildChanged(indexRef, async (snapshot) => {
      const entry = convertRtdbToFirestore(snapshot.val());
      const freeText = await fetchFreeText(entry);
      callback({
        type: 'changed',
        id: snapshot.key,
        ...entry,
        freeText
      });
    });
    unsubscribes.push(onChangedUnsubscribe);
    
    // 入場情報の削除
    const onRemovedUnsubscribe = onChildRemoved(indexRef, (snapshot) => {
      callback({
        type: 'removed',
        id: snapshot.key
      });
    });
    unsubscribes.push(onRemovedUnsubscribe);
    
    // すべてのリスナーを解除する関数を返す
    return () => {
      unsubscribes.forEach(unsub => unsub());
    };
  } catch (error) {
    console.error('Subscribe to QR entries setup failed:', error);
    return () => {}; // no-op unsubscribe
  }
};

/**
 * ユーザーのチケット一覧をリアルタイム監視
 * @param {string} userId - ユーザーID
 * @param {function} callback - 更新時のコールバック関数
 * @returns {function} - unsubscribe関数
 */
export const subscribeToUserTickets = (userId, callback) => {
  if (!userId || typeof userId !== 'string' || !userId.trim()) {
    return () => {};
  }

  try {
    const { rtdb } = initFirebaseTickets();
    const normalizedUserId = userId.trim();
    const ticketsRef = ref(rtdb, getTicketsPath());
    const q = rtdbQuery(ticketsRef, orderByChild('createdBy'), equalTo(normalizedUserId));
    
    const unsubscribe = onValue(q, (snapshot) => {
      if (snapshot.exists()) {
        const tickets = snapshotToArray(snapshot).map(ticket => 
          convertRtdbToFirestore(ticket)
        );
        callback(tickets);
      } else {
        callback([]);
      }
    }, (error) => {
      console.error('Subscribe to user tickets failed:', error);
      callback([]);
    });
    
    return unsubscribe;
  } catch (error) {
    console.error('Subscribe to user tickets setup failed:', error);
    return () => {}; // no-op unsubscribe
  }
};

/**
 * QRチャンクをリアルタイム監視（ページネーション用）
 * @param {string} ticketId - チケットID
 * @param {string} chunkId - チャンクID
 * @param {function} callback - 更新時のコールバック関数
 * @returns {function} - unsubscribe関数
 */
export const subscribeToQRChunk = (ticketId, chunkId, callback) => {
  try {
    const { rtdb } = initFirebaseTickets();
    const chunkRef = ref(rtdb, getQRChunksPath(ticketId, chunkId));
    
    const unsubscribe = onValue(chunkRef, (snapshot) => {
      if (snapshot.exists()) {
        const chunk = convertRtdbToFirestore(snapshot.val());
        callback(chunk);
      } else {
        callback(null);
      }
    }, (error) => {
      console.error('Subscribe to QR chunk failed:', error);
      callback(null);
    });
    
    return unsubscribe;
  } catch (error) {
    console.error('Subscribe to QR chunk setup failed:', error);
    return () => {}; // no-op unsubscribe
  }
};

/**
 * 全QRチャンクをリアルタイム監視
 * @param {string} ticketId - チケットID
 * @param {function} callback - 更新時のコールバック関数
 * @returns {function} - unsubscribe関数
 */
export const subscribeToAllQRChunks = (ticketId, callback) => {
  try {
    const { rtdb } = initFirebaseTickets();
    const chunksRef = ref(rtdb, getQRChunksPath(ticketId));
    
    const unsubscribe = onValue(chunksRef, (snapshot) => {
      if (snapshot.exists()) {
        const chunks = snapshotToArray(snapshot).map(chunk => 
          convertRtdbToFirestore(chunk)
        );
        callback(chunks);
      } else {
        callback([]);
      }
    }, (error) => {
      console.error('Subscribe to all QR chunks failed:', error);
      callback([]);
    });
    
    return unsubscribe;
  } catch (error) {
    console.error('Subscribe to all QR chunks setup failed:', error);
    return () => {}; // no-op unsubscribe
  }
};

/**
 * ユーザーが作成したチケット一覧取得
 */
export const getUserTickets = async (userId) => {
  if (!userId || typeof userId !== 'string' || !userId.trim()) {
    return [];
  }

  const normalizedUserId = userId.trim();
  
  try {
    const { rtdb } = initFirebaseTickets();
    const ticketsRef = ref(rtdb, getTicketsPath());
    const q = rtdbQuery(ticketsRef, orderByChild('createdBy'), equalTo(normalizedUserId));
    
    console.log('[getUserTickets] Querying RTDB for user:', normalizedUserId);
    const snapshot = await get(q);
    
    if (!snapshot.exists()) {
      console.log('[getUserTickets] No tickets found in RTDB');
      return [];
    }
    
    const tickets = snapshotToArray(snapshot).map(ticket => {
      const converted = convertRtdbToFirestore(ticket);
      console.log('[getUserTickets] Ticket from RTDB:', ticket.id, 'usedQRCodes:', converted.usedQRCodes);
      return converted;
    });
    
    console.log('[getUserTickets] Found', tickets.length, 'tickets in RTDB');
    
    // ローカルストレージに保存
    writeLocalTickets(tickets);
    
    return tickets;
  } catch (error) {
    console.error('[getUserTickets] RTDB query failed:', error);
    
    // フォールバック: ローカルストレージから読み取り
    console.warn('[getUserTickets] Falling back to localStorage');
    return readLocalTickets().filter(ticket => ticket.createdBy === normalizedUserId);
  }
};

/**
 * チケット更新
 */
export const updateTicket = async (ticketId, updates) => {
  try {
    const { rtdb } = initFirebaseTickets();
    const ticketRef = ref(rtdb, getTicketsPath(ticketId));
    
    const data = convertFirestoreToRtdb(updates);
    console.log('[updateTicket] Updating ticket in RTDB:', ticketId, data);
    await update(ticketRef, data);
    console.log('[updateTicket] Ticket updated successfully');

    // リアルタイムリスナーが自動的に更新するため、ローカルストレージへの書き込みは不要
    
    return true;
  } catch (error) {
    if (isCommunicationError(error)) {
      const retryError = new Error('通信エラーが発生しました。更新を再試行してください。');
      retryError.code = normalizeErrorCode(error) || 'network-request-failed';
      throw retryError;
    }

    console.warn('Update ticket fallback to local:', error);

    const localTickets = readLocalTickets();
    const index = localTickets.findIndex(ticket => ticket.id === ticketId);
    if (index >= 0) {
      const next = [...localTickets];
      next[index] = { ...next[index], ...updates };
      writeLocalTickets(next);
      return true;
    }

    return false;
  }
};

/**
 * チケット削除
 */
export const deleteTicket = async (ticketId) => {
  const cleanupLocalTicketAndQRCodes = () => {
    if (typeof localStorage !== 'undefined') {
      try {
        localStorage.removeItem(`localQRCodes:${ticketId}`);
      } catch {
        // no-op
      }
    }

    const localTickets = readLocalTickets();
    const filtered = localTickets.filter(ticket => ticket.id !== ticketId);
    if (filtered.length !== localTickets.length) {
      writeLocalTickets(filtered);
    }
  };

  try {
    const { rtdb } = initFirebaseTickets();
    
    // QRコード関連のサブコレクションを削除
    const qrPaths = [
      getQRIndexPath(ticketId),
      getQRChunksPath(ticketId),
      getQRCodesPath(ticketId),
      getPermissionsPath(ticketId)
    ];

    for (const qrPath of qrPaths) {
      const qrRef = ref(rtdb, qrPath);
      const snapshot = await get(qrRef);
      
      if (snapshot.exists()) {
        await remove(qrRef);
      }
    }

    // チケット本体を削除
    const ticketRef = ref(rtdb, getTicketsPath(ticketId));
    await remove(ticketRef);

    cleanupLocalTicketAndQRCodes();
    return true;
  } catch (error) {
    console.warn('Delete ticket fallback to local cleanup:', error);

    cleanupLocalTicketAndQRCodes();

    const code = error?.code || '';
    if (
      code === 'permission-denied' ||
      code === 'unauthenticated' ||
      code === 'failed-precondition'
    ) {
      return false;
    }

    return false;
  }
};

// ========================================
// QR コード操作
// ========================================

/**
 * QRコード一括生成・保存
 */
export const generateAndSaveQRCodes = async (ticketId, qrCodes) => {
  try {
    const { rtdb } = initFirebaseTickets();
    const ticketRef = ref(rtdb, getTicketsPath(ticketId));
    const ticketSnap = await get(ticketRef);
    const currentIssued = Number(ticketSnap.val()?.issuedCount ?? ticketSnap.val()?.usedQRCodes ?? 0);
    const qrDeleteAt = createDeleteAtAfterOneYear();

    const chunks = splitArrayBySize(qrCodes, QR_CHUNK_SIZE);

    const updates = {};
    let updateCount = 0;

    for (let chunkOffset = 0; chunkOffset < chunks.length; chunkOffset += 1) {
      const chunk = chunks[chunkOffset];
      const chunkStartIndex = currentIssued + chunkOffset * QR_CHUNK_SIZE;
      const chunkId = createChunkId(chunkStartIndex);
      
      // QRコードのデータを保存（imageDataはRTDBに保存しない - サイズ制限のため）
      const chunkCodesData = chunk.map((item, idx) => ({
        code: item.code,
        // imageData: item.imageData || null,  // RTDBには保存しない
        scanCount: 0,
        entryStatus: 'unused',
        freeText: item.freeText || ''
      }));

      const chunkPath = getQRChunksPath(ticketId, chunkId);
      updates[chunkPath] = convertFirestoreToRtdb({
        chunkId,
        startIndex: chunkStartIndex,
        endIndex: chunkStartIndex + chunkCodesData.length - 1,
        count: chunkCodesData.length,
        codes: chunkCodesData,  // ← 完全なデータを保存
        deleteAt: qrDeleteAt,
        createdAt: rtdbServerTimestamp(),
        updatedAt: rtdbServerTimestamp()
      });
      updateCount += 1;

      // qr_index への事前書き込みを削除（入場時に lazy creation する）
      // これにより、QRコード生成時の書き込み回数を大幅に削減
    }

    updates[`${getTicketsPath(ticketId)}/usedQRCodes`] = rtdbIncrement(qrCodes.length);
    updates[`${getTicketsPath(ticketId)}/issuedCount`] = rtdbIncrement(qrCodes.length);
    updates[`${getTicketsPath(ticketId)}/updatedAt`] = rtdbServerTimestamp();

    await update(ref(rtdb), updates);

    const existingLocalQRCodes = readLocalQRCodes(ticketId);
    const nowIso = new Date().toISOString();
    const mergedLocalQRCodes = [
      ...existingLocalQRCodes,
      ...qrCodes.map((qrCode) => ({
        code: qrCode.code,
        createdAt: nowIso,
        deleteAt: qrDeleteAt.toISOString(),
        scanHistory: [],
        entryStatus: 'unused',
        enteredAt: null,
        enteredByDevice: null
      }))
    ];
    writeLocalQRCodes(ticketId, mergedLocalQRCodes);

    const localTickets = readLocalTickets();
    const ticketIndex = localTickets.findIndex((ticket) => ticket.id === ticketId);
    if (ticketIndex >= 0) {
      const nextTickets = [...localTickets];
      const currentUsed = Number(nextTickets[ticketIndex].usedQRCodes || 0);
      nextTickets[ticketIndex] = {
        ...nextTickets[ticketIndex],
        usedQRCodes: currentUsed + qrCodes.length
      };
      writeLocalTickets(nextTickets);
    }
    
    return {
      count: qrCodes.length,
      storage: 'rtdb'
    };
  } catch (error) {
    if (isCommunicationError(error)) {
      const retryError = new Error('通信エラーが発生したためQR保存に失敗しました。再試行してください。');
      retryError.code = normalizeErrorCode(error) || 'network-request-failed';
      throw retryError;
    }

    console.warn('Generate QR codes fallback to local:', error);
    const errorCode = error?.code || 'unknown';
    const errorMessage = error?.message || 'No error message';

    if (typeof localStorage !== 'undefined') {
      try {
        const key = `localQRCodes:${ticketId}`;
        const existingRaw = localStorage.getItem(key);
        const existing = existingRaw ? JSON.parse(existingRaw) : [];
        const safeExisting = Array.isArray(existing) ? existing : [];

        const now = new Date().toISOString();
        const toStore = qrCodes.map((qrCode) => ({
          code: qrCode.code,
          createdAt: now,
          deleteAt: createDeleteAtAfterOneYear().toISOString(),
          scanHistory: [],
          entryStatus: 'unused',
          enteredAt: null,
          enteredByDevice: null
        }));

        localStorage.setItem(key, JSON.stringify([...safeExisting, ...toStore]));

        const localTickets = readLocalTickets();
        const ticketIndex = localTickets.findIndex((ticket) => ticket.id === ticketId);
        if (ticketIndex >= 0) {
          const nextTickets = [...localTickets];
          const currentUsed = Number(nextTickets[ticketIndex].usedQRCodes || 0);
          nextTickets[ticketIndex] = {
            ...nextTickets[ticketIndex],
            usedQRCodes: currentUsed + qrCodes.length
          };
          writeLocalTickets(nextTickets);
        }
      } catch (storageError) {
        console.warn('Store local QR failed:', storageError);
      }
    }

    return {
      count: qrCodes.length,
      storage: 'local',
      errorCode,
      errorMessage
    };
  }
};

/**
 * 保存済みQRコード一覧を取得（印刷・再出力向け）
 */
export const getStoredQRCodes = async (ticketId, options = {}) => {
  const preferLocal = options?.preferLocal !== false;
  const localQRCodes = readLocalQRCodes(ticketId);
  
  console.log('[getStoredQRCodes] ticketId:', ticketId, 'preferLocal:', preferLocal, 'localQRCodes:', localQRCodes.length);
  
  // preferLocal: trueでも差分同期は実行する（入場状態を最新に保つため）
  // ローカルキャッシュは画像データとマージ元として使用
  
  // 最終同期時刻のキーを定義（関数全体で使用）
  const lastSyncKey = `qr_last_sync_${ticketId}`;

  try {
    const { rtdb } = initFirebaseTickets();

    const chunksRef = ref(rtdb, getQRChunksPath(ticketId));
    const chunkSnapshot = await get(chunksRef);
    console.log('[getStoredQRCodes] Chunks found:', chunkSnapshot.exists());
    
    let docs = [];

    if (chunkSnapshot.exists()) {
      // まずローカルストレージから取得
      const localQRCodes = readLocalQRCodes(ticketId);
      const localQRMap = new Map(localQRCodes.map(qr => [qr.code, qr]));
      console.log('[getStoredQRCodes] Local QR map size:', localQRMap.size);
      
      // 最終同期時刻を取得（ローカルストレージから）
      const lastSyncTime = localStorage.getItem(lastSyncKey);
      const lastSync = lastSyncTime ? new Date(lastSyncTime) : null;
      console.log('[getStoredQRCodes] Last sync:', lastSync);
      
      // qr_chunksからQRコード一覧を取得
      const chunksArray = snapshotToArray(chunkSnapshot);
      console.log('[getStoredQRCodes] Chunks array length:', chunksArray.length);
      
      const sortedChunks = chunksArray
        .map((chunk) => convertRtdbToFirestore(chunk))
        .sort((a, b) => Number(a.startIndex || 0) - Number(b.startIndex || 0));
      
      console.log('[getStoredQRCodes] First chunk codes sample:', sortedChunks[0]?.codes?.slice(0, 2));

      const allCodes = sortedChunks.flatMap((chunkDoc) => {
        const codes = Array.isArray(chunkDoc.codes) ? chunkDoc.codes : [];
        return codes.map((codeItem, index) => {
          // codeItemは文字列またはオブジェクト
          const isObject = typeof codeItem === 'object' && codeItem !== null;
          let codeString = isObject ? codeItem.code : codeItem;
          
          // codeが文字列でない場合は文字列に変換（ネストされたオブジェクトの場合も対応）
          if (typeof codeString !== 'string') {
            if (codeString && typeof codeString === 'object') {
              // codeString自体がオブジェクトの場合（ネストされた構造）
              codeString = codeString.value || codeString.code || codeString.text || String(codeString);
            } else {
              console.warn('[getStoredQRCodes] Invalid code type:', typeof codeString, codeString);
              codeString = String(codeString);
            }
          }
          
          // freeTextのデバッグ
          const freeTextValue = isObject ? (codeItem.freeText || '') : '';
          if (index < 3) {
            console.log('[getStoredQRCodes] freeText debug:', {
              index,
              code: codeString,
              isObject,
              rawFreeText: codeItem.freeText,
              freeTextValue,
              codeItem
            });
          }
          
          return {
            code: codeString,
            freeText: freeTextValue,
            scanCount: isObject ? (codeItem.scanCount || 0) : 0,
            entryStatus: isObject ? (codeItem.entryStatus || 'unused') : 'unused',
            chunkId: chunkDoc.chunkId || chunkDoc.id,
            offsetInChunk: index,
            createdAt: normalizeDateLike(chunkDoc.createdAt) || null
          };
        });
      });

      // 前回同期以降に更新されたqr_indexのみ取得
      let indexMap = new Map();
      const indexRef = ref(rtdb, getQRIndexPath(ticketId));
      const indexSnapshot = await get(indexRef);
      
      if (indexSnapshot.exists()) {
        indexSnapshot.forEach((childSnapshot) => {
          const data = convertRtdbToFirestore(childSnapshot.val());
          // 入場済み、またはコメントがあるものを取得
          if (data.entryStatus === 'entered' || data.comment) {
            indexMap.set(childSnapshot.key, data);
          }
        });
      }
      
      console.log('[getStoredQRCodes] Index map size (entered only):', indexMap.size);

      // ローカルとFirestoreの情報をマージ
      docs = allCodes.map((item) => {
        const indexDocId = createQRIndexDocId(item.code);
        const indexDoc = indexMap.get(indexDocId);
        const localDoc = localQRMap.get(item.code);

        // Firestoreの入場情報が最新（差分取得で取れたもの）
        if (indexDoc) {
          return {
            id: indexDocId,
            code: item.code,
            freeText: item.freeText || localDoc?.freeText || '',
            chunkId: item.chunkId,
            offsetInChunk: item.offsetInChunk,
            entryStatus: indexDoc.entryStatus,
            enteredAt: normalizeDateLike(indexDoc.enteredAt) || null,
            enteredByDevice: indexDoc.enteredByDevice || null,
            scanCount: Number(indexDoc.scanCount || 0),
            lastScanAt: normalizeDateLike(indexDoc.lastScanAt) || null,
            createdAt: normalizeDateLike(indexDoc.createdAt || item.createdAt) || null,
            updatedAt: normalizeDateLike(indexDoc.updatedAt) || null,
            comment: indexDoc.comment || localDoc?.comment || null,
            scanHistory: []
          };
        }

        // ローカルの情報を使用（差分に含まれていない = 前回から変更なし）
        if (localDoc) {
          return {
            id: indexDocId,
            code: item.code,
            freeText: item.freeText || localDoc.freeText || '',
            chunkId: item.chunkId,
            offsetInChunk: item.offsetInChunk,
            entryStatus: localDoc.entryStatus || 'unused',
            enteredAt: localDoc.enteredAt || null,
            enteredByDevice: localDoc.enteredByDevice || null,
            scanCount: Number(localDoc.scanCount || 0),
            lastScanAt: localDoc.lastScanAt || null,
            createdAt: item.createdAt,
            updatedAt: localDoc.updatedAt || null,
            comment: localDoc.comment || null,
            scanHistory: localDoc.scanHistory || []
          };
        }

        // デフォルト（未入場）
        return {
          id: indexDocId,
          code: item.code,
          freeText: item.freeText || '',
          chunkId: item.chunkId,
          offsetInChunk: item.offsetInChunk,
          entryStatus: 'unused',
          enteredAt: null,
          enteredByDevice: null,
          scanCount: 0,
          lastScanAt: null,
          createdAt: item.createdAt,
          updatedAt: null,
          comment: null,
          scanHistory: []
        };
      });
    } else {
      const legacyRef = ref(rtdb, getQRCodesPath(ticketId));
      const legacySnapshot = await get(legacyRef);
      
      if (legacySnapshot.exists()) {
        docs = snapshotToArray(legacySnapshot).map(qrDoc => {
          const data = convertRtdbToFirestore(qrDoc);
          
          // codeが文字列でない場合は文字列に変換
          let codeString = data.code;
          if (typeof codeString !== 'string') {
            if (codeString && typeof codeString === 'object') {
              codeString = codeString.value || codeString.code || codeString.text || String(codeString);
            } else {
              codeString = String(codeString);
            }
          }
          
          return {
            ...data,
            code: codeString,
            scanCount: Number(data?.scanCount ?? data?.scanHistory?.length ?? 0)
          };
        });
      }
    }

    console.log('[getStoredQRCodes] Total docs after merge:', docs.length);

    if (docs.length === 0) {
      console.log('[getStoredQRCodes] No docs found, returning empty array');
      return [];
    }

    // imageDataはlocalStorageに保存せず、毎回QRコードから生成する（容量節約）
    writeLocalQRCodes(ticketId, docs);
    localStorage.setItem(lastSyncKey, new Date().toISOString());

    const QRCode = await import('qrcode');
    const withGeneratedImages = await Promise.all(
      docs.map(async (item) => {
        // codeが文字列でない場合は文字列に変換
        let codeString = item.code;
        if (typeof codeString !== 'string') {
          if (codeString && typeof codeString === 'object') {
            codeString = codeString.value || codeString.code || codeString.text || String(codeString);
          } else {
            codeString = String(codeString);
          }
        }

        const imageData = await QRCode.toDataURL(codeString, {
          errorCorrectionLevel: 'H',
          type: 'image/png',
          width: 300,
          margin: 2,
          color: {
            dark: '#000000',
            light: '#ffffff'
          }
        });

        return {
          ...item,
          code: codeString,
          imageData
        };
      })
    );

    console.log('[getStoredQRCodes] Generated images, returning:', withGeneratedImages.length);
    return withGeneratedImages;
  } catch (error) {
    console.warn('Get stored QR codes fallback to local:', error);
    
    // インデックスエラーやその他のエラー時はローカルキャッシュを返す
    const localQRCodes = readLocalQRCodes(ticketId);
    if (localQRCodes.length > 0) {
      console.log('[getStoredQRCodes] Returning local cache due to error:', localQRCodes.length);
      // ローカルキャッシュにもimageDataがないので生成する
      try {
        const QRCode = await import('qrcode');
        const withImages = await Promise.all(
          localQRCodes.map(async (item) => {
            let codeString = typeof item.code === 'string' ? item.code : String(item.code);
            const imageData = await QRCode.toDataURL(codeString, {
              errorCorrectionLevel: 'H', type: 'image/png', width: 300, margin: 2,
              color: { dark: '#000000', light: '#ffffff' }
            });
            return { ...item, imageData };
          })
        );
        return withImages;
      } catch {
        return localQRCodes;
      }
    }
    
    if (isCommunicationError(error)) {
      const retryError = new Error('通信エラーが発生したため保存済みQRを取得できません。再試行してください。');
      retryError.code = normalizeErrorCode(error) || 'network-request-failed';
      throw retryError;
    }

    return [];
  }
};

/**
 * QRコード一覧をリアルタイム監視（最適化版）
 * チャンクとqr_indexを同時に監視し、変更があった部分だけ更新
 * @param {string} ticketId - チケットID
 * @param {function} callback - 更新時のコールバック関数 (qrCodes) => void
 * @returns {function} - unsubscribe関数
 */
export const subscribeToQRCodes = (ticketId, callback) => {
  try {
    const { rtdb } = initFirebaseTickets();
    const unsubscribes = [];
    
    // ローカルキャッシュから初期データを取得
    const localQRCodes = readLocalQRCodes(ticketId);
    const qrCodesMap = new Map(localQRCodes.map(qr => [qr.code, qr]));
    
    // チャンク一覧を監視（QRコードの追加・削除）
    const chunksRef = ref(rtdb, getQRChunksPath(ticketId));
    const chunksUnsubscribe = onValue(chunksRef, (snapshot) => {
      if (snapshot.exists()) {
        const chunks = snapshotToArray(snapshot).map(chunk => 
          convertRtdbToFirestore(chunk)
        );
        
        // チャンクからQRコード一覧を生成
        const allCodes = chunks
          .sort((a, b) => Number(a.startIndex || 0) - Number(b.startIndex || 0))
          .flatMap((chunkDoc) => {
            const codes = Array.isArray(chunkDoc.codes) ? chunkDoc.codes : [];
            return codes.map((code, index) => ({
              code,
              chunkId: chunkDoc.chunkId || chunkDoc.id,
              offsetInChunk: index,
              createdAt: chunkDoc.createdAt || null
            }));
          });
        
        // 既存のマップを更新
        allCodes.forEach(item => {
          if (!qrCodesMap.has(item.code)) {
            qrCodesMap.set(item.code, {
              id: createQRIndexDocId(item.code),
              ...item,
              entryStatus: 'unused',
              enteredAt: null,
              enteredByDevice: null,
              scanCount: 0
            });
          }
        });
        
        // コールバックを呼ぶ
        callback(Array.from(qrCodesMap.values()));
        
        // ローカルストレージに保存
        writeLocalQRCodes(ticketId, Array.from(qrCodesMap.values()));
      }
    });
    unsubscribes.push(chunksUnsubscribe);
    
    // qr_indexを監視（入場状態の変更）
    const indexRef = ref(rtdb, getQRIndexPath(ticketId));
    
    // 新しい入場
    const onAddedUnsubscribe = onChildAdded(indexRef, (snapshot) => {
      const entry = convertRtdbToFirestore(snapshot.val());
      const code = entry.code || decodeQRCode(snapshot.key);
      
      if (qrCodesMap.has(code)) {
        qrCodesMap.set(code, {
          ...qrCodesMap.get(code),
          ...entry,
          id: snapshot.key
        });
      } else {
        qrCodesMap.set(code, {
          id: snapshot.key,
          code,
          ...entry
        });
      }
      
      callback(Array.from(qrCodesMap.values()));
      writeLocalQRCodes(ticketId, Array.from(qrCodesMap.values()));
    });
    unsubscribes.push(onAddedUnsubscribe);
    
    // 入場状態の更新
    const onChangedUnsubscribe = onChildChanged(indexRef, (snapshot) => {
      const entry = convertRtdbToFirestore(snapshot.val());
      const code = entry.code || decodeQRCode(snapshot.key);
      
      if (qrCodesMap.has(code)) {
        qrCodesMap.set(code, {
          ...qrCodesMap.get(code),
          ...entry,
          id: snapshot.key
        });
        
        callback(Array.from(qrCodesMap.values()));
        writeLocalQRCodes(ticketId, Array.from(qrCodesMap.values()));
      }
    });
    unsubscribes.push(onChangedUnsubscribe);
    
    // すべてのリスナーを解除する関数を返す
    return () => {
      unsubscribes.forEach(unsub => unsub());
    };
  } catch (error) {
    console.error('Subscribe to QR codes setup failed:', error);
    return () => {}; // no-op unsubscribe
  }
};

/**
 * QRコード検索（スキャン時）
 */
export const findQRCode = async (ticketId, qrCode) => {
  try {
    // QRコードを大文字に正規化（大文字小文字を区別しない）
    const normalizedQRCode = qrCode.toUpperCase();
    
    console.log('[findQRCode] Searching for QR:', {
      ticketId,
      originalQRCode: qrCode,
      normalizedQRCode
    });
    
    const localMatch = readLocalQRCodes(ticketId).find((item) => item?.code && item.code.toUpperCase() === normalizedQRCode);
    if (localMatch) {
      console.log('[findQRCode] Found in local cache:', localMatch);
      return {
        id: localMatch.id || createQRIndexDocId(normalizedQRCode),
        ...localMatch
      };
    }

    const { rtdb } = initFirebaseTickets();
    
    // まず qr_index から位置情報を取得
    const indexDocId = createQRIndexDocId(normalizedQRCode);
    const indexRef = ref(rtdb, getQRIndexPath(ticketId, normalizedQRCode));
    const indexSnap = await get(indexRef);
    
    console.log('[findQRCode] qr_index check:', {
      exists: indexSnap.exists(),
      path: getQRIndexPath(ticketId, normalizedQRCode)
    });

    if (indexSnap.exists()) {
      const indexData = convertRtdbToFirestore(indexSnap.val());
      console.log('[findQRCode] Found in qr_index:', indexData);
      
      // qr_chunks から実際のQRコードデータを取得（freeTextを含む）
      if (indexData.chunkId != null && indexData.offsetInChunk != null) {
        const chunkRef = ref(rtdb, `${getTicketsPath(ticketId)}/qr_chunks/${indexData.chunkId}`);
        const chunkSnap = await get(chunkRef);
        
        if (chunkSnap.exists()) {
          const chunk = chunkSnap.val();
          const qrData = chunk.codes?.[indexData.offsetInChunk];
          
          if (qrData) {
            return {
              id: indexDocId,
              ...indexData,
              freeText: qrData.freeText || ''
            };
          }
        }
      }
      
      return {
        id: indexDocId,
        ...indexData
      };
    }
    
    // qr_index にない場合、qr_chunks を直接検索
    console.log('[findQRCode] Not in qr_index, searching qr_chunks...');
    const chunksRef = ref(rtdb, getQRChunksPath(ticketId));
    const chunksSnapshot = await get(chunksRef);
    
    console.log('[findQRCode] qr_chunks check:', {
      exists: chunksSnapshot.exists(),
      path: getQRChunksPath(ticketId)
    });
    
    if (chunksSnapshot.exists()) {
      const chunks = chunksSnapshot.val();
      console.log('[findQRCode] qr_chunks data:', {
        chunkCount: Object.keys(chunks).length,
        chunkIds: Object.keys(chunks)
      });
      
      for (const chunkId in chunks) {
        const chunk = chunks[chunkId];
        const codes = chunk.codes || [];
        
        console.log('[findQRCode] Checking chunk:', {
          chunkId,
          codeCount: codes.length,
          firstFewCodes: codes.slice(0, 3).map(c => typeof c === 'string' ? c : c?.code)
        });
        
        for (let i = 0; i < codes.length; i++) {
          const codeItem = codes[i];
          const codeString = typeof codeItem === 'string' ? codeItem : codeItem?.code;
          
          // 大文字小文字を区別しない比較
          if (codeString && codeString.toUpperCase() === normalizedQRCode) {
            console.log('[findQRCode] Found in qr_chunks:', {
              chunkId,
              offsetInChunk: i,
              code: codeString
            });
            return {
              id: createQRIndexDocId(normalizedQRCode),
              code: codeString,
              chunkId: chunkId,
              offsetInChunk: i,
              entryStatus: codeItem?.entryStatus || 'unused',
              enteredAt: codeItem?.enteredAt || null,
              enteredByDevice: codeItem?.enteredByDevice || null,
              freeText: codeItem?.freeText || '',
              scanCount: codeItem?.scanCount || 0
            };
          }
        }
      }
      console.log('[findQRCode] QR code not found in any chunk');
    }

    // レガシー qr_codes から検索
    console.log('[findQRCode] Searching legacy qr_codes...');
    const legacyRef = ref(rtdb, getQRCodesPath(ticketId));
    const legacySnapshot = await get(legacyRef);
    
    if (legacySnapshot.exists()) {
      let foundQR = null;
      legacySnapshot.forEach((childSnapshot) => {
        const data = convertRtdbToFirestore(childSnapshot.val());
        // 大文字小文字を区別しない比較
        if (data.code && data.code.toUpperCase() === normalizedQRCode) {
          foundQR = {
            id: childSnapshot.key,
            ...data
          };
        }
      });
      if (foundQR) {
        console.log('[findQRCode] Found in legacy qr_codes:', foundQR);
      }
      return foundQR;
    }
    
    console.log('[findQRCode] QR code not found anywhere');
    return null;
  } catch (error) {
    console.error('Find QR code failed:', error);
    throw error;
  }
};

/**
 * QRコードスキャン履歴記録
 */
export const recordQRScan = async (ticketId, qrCodeId, deviceUniqueId) => {
  try {
    const { rtdb } = initFirebaseTickets();

    const qrRef = ref(rtdb, getQRIndexPath(ticketId, qrCodeId));
    const qrSnap = await get(qrRef);

    if (qrSnap.exists()) {
      const currentData = qrSnap.val();
      const nextCount = Number(currentData.scanCount || 0) + 1;

      await update(qrRef, {
        scanCount: nextCount,
        lastScanAt: rtdbServerTimestamp(),
        updatedAt: rtdbServerTimestamp()
      });

      return {
        scanCount: nextCount,
        scanHistory: []
      };
    }

    const legacyRef = ref(rtdb, `${getQRCodesPath(ticketId)}/${qrCodeId}`);
    const legacySnap = await get(legacyRef);
    if (!legacySnap.exists()) throw new Error('QR code not found');

    const currentData = legacySnap.val();
    const scanHistory = currentData.scanHistory || [];
    scanHistory.push({
      scanTime: new Date().toISOString(),
      scanCount: scanHistory.length + 1,
      lastAccessTime: new Date().toISOString(),
      deviceUniqueId: deviceUniqueId
    });

    await update(legacyRef, {
      scanHistory: scanHistory
    });

    return {
      scanCount: scanHistory.length,
      scanHistory: scanHistory
    };
  } catch (error) {
    console.error('Record scan failed:', error);
    throw error;
  }
};

/**
 * 入場処理ベース（QR読み取り結果を確定）
 * - 未使用: 入場許可して使用済みに更新
 * - 使用済み: 二重入場として拒否
 */
export const processTicketEntry = async (ticketId, qrCode, deviceUniqueId = 'unknown-device') => {
  try {
    const qrData = await findQRCode(ticketId, qrCode);

    if (!qrData) {
      return {
        success: false,
        status: 'not_found',
        message: 'QRコードが見つかりません'
      };
    }

    if (qrData.entryStatus === 'entered') {
      return {
        success: false,
        status: 'already_entered',
        message: 'このQRは既に入場処理済みです',
        qrCodeId: qrData.id,
        enteredAt: qrData.enteredAt || null
      };
    }

    const { rtdb } = initFirebaseTickets();
    const qrIndexRef = ref(rtdb, getQRIndexPath(ticketId, qrData.code || qrCode));
    const qrIndexSnap = await get(qrIndexRef);

    const updates = {};
    
    if (qrIndexSnap.exists()) {
      // 既存の qr_index を更新
      updates[getQRIndexPath(ticketId, qrData.code || qrCode)] = {
        ...qrIndexSnap.val(),
        entryStatus: 'entered',
        enteredAt: rtdbServerTimestamp(),
        enteredByDevice: deviceUniqueId,
        scanCount: rtdbIncrement(1),
        lastScanAt: rtdbServerTimestamp(),
        updatedAt: rtdbServerTimestamp()
      };
    } else {
      // qr_index が存在しない場合は新規作成（lazy creation）
      updates[getQRIndexPath(ticketId, qrData.code || qrCode)] = {
        code: qrData.code || qrCode,
        chunkId: qrData.chunkId || null,
        offsetInChunk: qrData.offsetInChunk || null,
        entryStatus: 'entered',
        enteredAt: rtdbServerTimestamp(),
        enteredByDevice: deviceUniqueId,
        scanCount: 1,
        lastScanAt: rtdbServerTimestamp(),
        createdAt: rtdbServerTimestamp(),
        updatedAt: rtdbServerTimestamp()
      };
    }

    updates[`${getTicketsPath(ticketId)}/checkedCount`] = rtdbIncrement(1);
    updates[`${getTicketsPath(ticketId)}/updatedAt`] = rtdbServerTimestamp();
    
    await update(ref(rtdb), updates);

    const localQRCodes = readLocalQRCodes(ticketId);
    const localIndex = localQRCodes.findIndex((item) => item?.code === qrCode);
    if (localIndex >= 0) {
      const next = [...localQRCodes];
      next[localIndex] = {
        ...next[localIndex],
        entryStatus: 'entered',
        enteredAt: new Date().toISOString(),
        enteredByDevice: deviceUniqueId
      };
      writeLocalQRCodes(ticketId, next);
    }

    const localTickets = readLocalTickets();
    const localTicketIndex = localTickets.findIndex((ticket) => ticket.id === ticketId);
    if (localTicketIndex >= 0) {
      const nextTickets = [...localTickets];
      const currentRead = Number(nextTickets[localTicketIndex].readCount || 0);
      nextTickets[localTicketIndex] = {
        ...nextTickets[localTicketIndex],
        readCount: currentRead + 1
      };
      writeLocalTickets(nextTickets);
    }

    return {
      success: true,
      status: 'entry_accepted',
      message: '入場処理が完了しました',
      qrCodeId: qrData.id,
      freeText: qrData.freeText || ''
    };
  } catch (error) {
    console.warn('Process ticket entry failed:', error);
    return {
      success: false,
      status: 'error',
      message: error.message
    };
  }
};

/**
 * QRコードのコメントを保存する
 * @param {string} ticketId - チケットID
 * @param {string} qrCode - QRコード
 * @param {string} comment - コメント文字列
 */
export const saveQRComment = async (ticketId, qrCode, comment) => {
  try {
    const qrData = await findQRCode(ticketId, qrCode);
    if (!qrData) {
      return { success: false, status: 'not_found', message: 'QRコードが見つかりません' };
    }

    const { rtdb } = initFirebaseTickets();
    const qrIndexPath = getQRIndexPath(ticketId, qrData.code || qrCode);
    const qrIndexRef = ref(rtdb, qrIndexPath);
    const qrIndexSnap = await get(qrIndexRef);

    const now = rtdbServerTimestamp();
    if (qrIndexSnap.exists()) {
      await update(qrIndexRef, { comment: comment || null, updatedAt: now });
    } else {
      await set(qrIndexRef, {
        code: qrData.code || qrCode,
        chunkId: qrData.chunkId || null,
        offsetInChunk: qrData.offsetInChunk || null,
        entryStatus: null,
        comment: comment || null,
        createdAt: now,
        updatedAt: now
      });
    }

    // ローカルキャッシュ更新
    const localQRCodes = readLocalQRCodes(ticketId);
    const localIndex = localQRCodes.findIndex((item) => item?.code === (qrData.code || qrCode));
    if (localIndex >= 0) {
      const next = [...localQRCodes];
      next[localIndex] = { ...next[localIndex], comment: comment || null };
      writeLocalQRCodes(ticketId, next);
    }

    return { success: true };
  } catch (error) {
    console.warn('[saveQRComment] failed:', error);
    return { success: false, status: 'error', message: error.message };
  }
};

/**
 * 手動で入場状態を設定・解除する
 * @param {string} ticketId - チケットID
 * @param {string} qrCode - QRコード
 * @param {string} targetStatus - 'entered' | 'unused'
 */
export const manualSetEntryStatus = async (ticketId, qrCode, targetStatus) => {
  try {
    const qrData = await findQRCode(ticketId, qrCode);
    if (!qrData) {
      return { success: false, status: 'not_found', message: 'QRコードが見つかりません' };
    }

    const { rtdb } = initFirebaseTickets();
    const qrIndexRef = ref(rtdb, getQRIndexPath(ticketId, qrData.code || qrCode));
    const qrIndexSnap = await get(qrIndexRef);

    const updates = {};
    const now = rtdbServerTimestamp();

    if (targetStatus === 'entered') {
      const existing = qrIndexSnap.exists() ? qrIndexSnap.val() : {};
      updates[getQRIndexPath(ticketId, qrData.code || qrCode)] = {
        ...existing,
        code: qrData.code || qrCode,
        chunkId: existing.chunkId || qrData.chunkId || null,
        offsetInChunk: existing.offsetInChunk || qrData.offsetInChunk || null,
        entryStatus: 'entered',
        enteredAt: now,
        enteredByDevice: 'manual',
        scanCount: (existing.scanCount || 0) + 1,
        lastScanAt: now,
        createdAt: existing.createdAt || now,
        updatedAt: now
      };
      updates[`${getTicketsPath(ticketId)}/checkedCount`] = rtdbIncrement(1);
    } else {
      // 入場解除
      if (qrIndexSnap.exists()) {
        const existing = qrIndexSnap.val();
        updates[getQRIndexPath(ticketId, qrData.code || qrCode)] = {
          ...existing,
          entryStatus: null,
          enteredAt: null,
          enteredByDevice: null,
          updatedAt: now
        };
        updates[`${getTicketsPath(ticketId)}/checkedCount`] = rtdbIncrement(-1);
      }
    }

    updates[`${getTicketsPath(ticketId)}/updatedAt`] = now;
    await update(ref(rtdb), updates);

    // ローカルキャッシュ更新
    const localQRCodes = readLocalQRCodes(ticketId);
    const localIndex = localQRCodes.findIndex((item) => item?.code === (qrData.code || qrCode));
    if (localIndex >= 0) {
      const next = [...localQRCodes];
      if (targetStatus === 'entered') {
        next[localIndex] = {
          ...next[localIndex],
          entryStatus: 'entered',
          enteredAt: new Date().toISOString(),
          enteredByDevice: 'manual'
        };
      } else {
        next[localIndex] = {
          ...next[localIndex],
          entryStatus: null,
          enteredAt: null,
          enteredByDevice: null
        };
      }
      writeLocalQRCodes(ticketId, next);
    }

    return { success: true, status: targetStatus === 'entered' ? 'entry_accepted' : 'entry_reverted' };
  } catch (error) {
    console.warn('[manualSetEntryStatus] failed:', error);
    return { success: false, status: 'error', message: error.message };
  }
};

// ========================================
// 権限管理
// ========================================

/**
 * 外部デバイス権限追加
 */
export const addExternalPermission = async (ticketId, deviceUniqueId, grantedBy) => {
  try {
    const { rtdb } = initFirebaseTickets();
    
    const permissionsRef = ref(rtdb, getPermissionsPath(ticketId));
    const newPermissionRef = push(permissionsRef);
    
    await set(newPermissionRef, {
      grantedDeviceId: deviceUniqueId,
      grantedAt: rtdbServerTimestamp(),
      grantedBy: grantedBy
    });
    
    return newPermissionRef.key;
  } catch (error) {
    console.error('Add permission failed:', error);
    throw error;
  }
};

/**
 * 外部デバイス権限削除
 */
export const removeExternalPermission = async (ticketId, permissionId) => {
  try {
    const { rtdb } = initFirebaseTickets();
    const permissionRef = ref(rtdb, getPermissionsPath(ticketId, permissionId));
    
    await remove(permissionRef);
  } catch (error) {
    console.error('Remove permission failed:', error);
    throw error;
  }
};

/**
 * 権限チェック（スタッフが読み取り可能か）
 */
export const checkQRAccessPermission = async (ticketId, userId) => {
  try {
    const { db } = initFirebaseTickets();
    
    // チケット情報取得
    const ticket = await getTicket(ticketId);
    if (!ticket) return false;
    
    // チケット作成者なら許可
    if (ticket.createdBy === userId) return true;
    
    // 外部デバイス許可が OFF なら拒否
    if (!ticket.externalAccessEnabled) return false;
    
    // 外部デバイス許可が ON ならアクセス許可
    return true;
  } catch (error) {
    console.error('Check permission failed:', error);
    throw error;
  }
};

// ========================================
// 課金関連 (RevenueCat との連携は別)
// ========================================

export const createSubscription = async (subscriptionData) => {
  try {
    const { db } = initFirebaseTickets();
    
    const docRef = await addDoc(collection(db, 'subscriptions'), {
      ...subscriptionData,
      purchasedAt: firestoreServerTimestamp(),
      status: 'active'
    });
    
    return docRef.id;
  } catch (error) {
    console.error('Create subscription failed:', error);
    throw error;
  }
};

export const getSubscription = async (subscriptionId) => {
  try {
    const { db } = initFirebaseTickets();
    const docRef = doc(db, 'subscriptions', subscriptionId);
    const docSnap = await getDoc(docRef);
    
    return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } : null;
  } catch (error) {
    console.error('Get subscription failed:', error);
    throw error;
  }
};

// サブスクリプションキャッシュ（1時間有効）
const subscriptionCache = new Map();
const SUBSCRIPTION_CACHE_TTL = 60 * 60 * 1000; // 1時間

/**
 * サブスクリプションキャッシュをクリア（購入後に呼び出す）
 */
export const clearSubscriptionCache = (userId) => {
  if (userId) {
    const normalizedUserId = userId.trim();
    subscriptionCache.delete(normalizedUserId);
    const lastSyncKey = `subscriptions_last_sync_${normalizedUserId}`;
    localStorage.removeItem(lastSyncKey);
    console.log(`[clearSubscriptionCache] Cleared cache for user: ${normalizedUserId}`);
  }
};

export const getUserSubscriptions = async (userId, forceRefresh = false) => {
  if (!userId || typeof userId !== 'string' || !userId.trim()) {
    return [];
  }

  const normalizedUserId = userId.trim();
  
  // キャッシュチェック（forceRefresh=falseの場合のみ）
  if (!forceRefresh) {
    const cached = subscriptionCache.get(normalizedUserId);
    if (cached && Date.now() - cached.timestamp < SUBSCRIPTION_CACHE_TTL) {
      console.log(`[getUserSubscriptions] Using memory cache for user: ${normalizedUserId}`);
      return cached.data;
    }
  }
  
  try {
    const { db } = initFirebaseTickets();
    // READ削減: ローカルキャッシュがある場合は、それを優先して返す
    const localCache = readLocalSubscriptions().filter((item) => item?.userId === normalizedUserId);
    
    // ローカルキャッシュが新しい場合（1時間以内）はFirestoreアクセスをスキップ
    const lastSyncKey = `subscriptions_last_sync_${normalizedUserId}`;
    const lastSync = localStorage.getItem(lastSyncKey);
    if (!forceRefresh && localCache.length > 0 && lastSync && Date.now() - new Date(lastSync).getTime() < SUBSCRIPTION_CACHE_TTL) {
      console.log(`[getUserSubscriptions] Using local cache (recent sync): ${localCache.length} subscriptions`);
      subscriptionCache.set(normalizedUserId, {
        data: localCache,
        timestamp: Date.now()
      });
      return localCache;
    }
    
    const q = query(
      collection(db, 'subscriptions'),
      where('userId', '==', normalizedUserId)
    );
    const snapshot = await getDocs(q);
    
    const subscriptions = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data()
    }));
    
    console.log(`[getUserSubscriptions] Loaded ${subscriptions.length} subscriptions from Firestore`);
    
    // Update local storage and cache
    if (subscriptions.length > 0) {
      writeLocalSubscriptions(subscriptions);
      localStorage.setItem(lastSyncKey, new Date().toISOString());
    }
    
    // メモリキャッシュに保存
    subscriptionCache.set(normalizedUserId, {
      data: subscriptions,
      timestamp: Date.now()
    });
    
    return subscriptions;
  } catch (error) {
    console.warn('[getUserSubscriptions] Firestore query failed, using local cache:', error);
    return readLocalSubscriptions().filter((item) => item?.userId === normalizedUserId);
  }
};

export const deleteUserSubscriptions = async (userId) => {
  const normalizedUserId = (userId || '').trim();
  if (!normalizedUserId) {
    return { deletedCount: 0 };
  }

  let deletedCount = 0;

  try {
    const { db } = initFirebaseTickets();
    const q = query(
      collection(db, 'subscriptions'),
      where('userId', '==', normalizedUserId)
    );
    const snapshot = await getDocs(q);

    const batch = writeBatch(db);
    snapshot.docs.forEach((docSnap) => {
      batch.delete(docSnap.ref);
      deletedCount += 1;
    });

    if (deletedCount > 0) {
      await batch.commit();
    }
  } catch (error) {
    const code = error?.code || '';
    if (code !== 'permission-denied' && code !== 'unauthenticated') {
      console.warn('Delete subscriptions fallback to local:', error);
    }
  }

  if (typeof localStorage !== 'undefined') {
    try {
      const key = 'localSubscriptions';
      const raw = localStorage.getItem(key);
      const parsed = raw ? JSON.parse(raw) : [];
      const safeParsed = Array.isArray(parsed) ? parsed : [];
      const filtered = safeParsed.filter((item) => item?.userId !== normalizedUserId);
      localStorage.setItem(key, JSON.stringify(filtered));
    } catch {
      // no-op
    }
  }

  return { deletedCount };
};

export const createStripeCheckoutSession = async ({
  plan,
  userId,
  quantity = 1,
  successUrl,
  cancelUrl,
  language = 'en' // 言語パラメータを追加（デフォルト: en）
}) => {
  console.log('[createStripeCheckoutSession] Input:', { plan, userId, quantity, successUrl, cancelUrl, language });
  
  // Product Masterから商品情報を取得
  const selectedPlan = await getProductFromMaster(plan);
  if (!selectedPlan) {
    throw new Error('Invalid plan');
  }

  const safeQuantity = Math.max(1, Number(quantity || 1));
  const { app, auth } = initFirebaseTickets();
  if (!auth?.currentUser) {
    const authError = new Error('ログインが必要です');
    authError.code = 'unauthenticated';
    throw authError;
  }

  // 環境情報を取得（config.jsから）
  const { environment } = await import('./config.js').then(m => m.config || m.default || {});
  
  const requestData = {
    plan,
    userId,
    quantity: safeQuantity,
    successUrl,
    cancelUrl,
    language, // 言語パラメータを追加
    environment: environment || 'dev' // 環境情報を追加
  };
  console.log('[createStripeCheckoutSession] Sending to Cloud Function:', requestData);

  const callable = httpsCallable(getFunctions(app, FUNCTIONS_REGION), 'createStripeCheckoutSession');
  const result = await callable(requestData);

  const sessionData = result?.data || {};
  console.log('[createStripeCheckoutSession] Response:', sessionData);
  
  if (!sessionData?.sessionId) {
    throw new Error('決済セッションの作成に失敗しました');
  }

  return sessionData;
};

export const verifyStripeCheckoutSession = async ({ sessionId }) => {
  const safeSessionId = String(sessionId || '').trim();
  if (!safeSessionId) {
    throw new Error('sessionId is required');
  }

  const { app, auth } = initFirebaseTickets();
  if (!auth?.currentUser) {
    const authError = new Error('ログインが必要です');
    authError.code = 'unauthenticated';
    throw authError;
  }

  const callable = httpsCallable(getFunctions(app, FUNCTIONS_REGION), 'verifyStripeCheckoutSession');
  const result = await callable({ sessionId: safeSessionId });
  return result?.data || { success: false, applied: false };
};

// ========================================
// QRコード生成（PC側専用）
// ========================================

/**
 * 8文字のランダム英数字（大文字）を生成
 */
const generateRandomCode = () => {
  const chars = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  let result = '';
  for (let i = 0; i < 8; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

/**
 * QRコード生成（複数枚）
 * @param {string} ticketId - チケットID
 * @param {number} count - 生成枚数
 * @returns {Promise<Array>} QRコード情報配列 [{id, code}, ...]
 */
export const generateQRCodeImages = async (ticketId, count) => {
  // PC側のみで使用（require 不可は許容、動的importで対応）
  if (typeof window === 'undefined') {
    throw new Error('This function must be called from PC/Web environment');
  }
  
  try {
    // 動的 import（PC側のみ）
    const QRCode = await import('qrcode');
    
    const qrCodes = [];
    const usedCodes = new Set(); // 同一バッチ内での重複チェック用
    
    for (let i = 0; i < count; i++) {
      // 同一バッチ内で重複しないコードを生成
      // 36^8 = 2,821,109,907,456 通りあるため、グローバルな重複確率は極めて低い
      let code;
      do {
        code = generateRandomCode();
      } while (usedCodes.has(code));
      usedCodes.add(code);
      
      // QRコードをData URL（PNG画像）に変換
      const dataUrl = await QRCode.toDataURL(code, {
        errorCorrectionLevel: 'H',
        type: 'image/png',
        width: 300,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#ffffff'
        }
      });
      
      qrCodes.push({
        id: `qr_${Date.now()}_${i}`,
        code: code,
        imageData: dataUrl,  // PNG Data URL
        timestamp: new Date().toISOString()
      });
    }
    
    return qrCodes;
  } catch (error) {
    console.error('Generate QR codes failed:', error);
    throw error;
  }
};

/**
 * QRコード画像をCSVリスト出力
 * @param {Array} qrCodes - QRコード配列
 * @returns {string} CSV文字列
 */
export const generateQRCodeCSV = (qrCodes, language = 'ja') => {
  // Import getTranslation if not already imported
  const { getTranslation } = require('./i18n');
  
  const headers = [
    getTranslation(language, 'tableHeaderQRCode'),
    getTranslation(language, 'tableHeaderStatus'),
    getTranslation(language, 'tableHeaderEntryTime'),
    getTranslation(language, 'tableHeaderDevice'),
    getTranslation(language, 'tableHeaderFreeText'),
    getTranslation(language, 'tableHeaderComment')
  ];
  const rows = qrCodes.map((qr, index) => {
    // codeが文字列でない場合は文字列に変換
    let codeString = qr.code;
    if (typeof codeString !== 'string') {
      if (codeString && typeof codeString === 'object') {
        codeString = codeString.value || codeString.code || codeString.text || String(codeString);
      } else {
        codeString = String(codeString);
      }
    }
    
    // 状態
    const status = qr?.entryStatus === 'entered' ? '入場済み' : '未入場';
    
    // 入場日時
    const enteredAt = qr?.enteredAt 
      ? new Date(qr.enteredAt).toLocaleString('ja-JP', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        })
      : '';
    
    // デバイス
    const device = qr?.enteredByDevice || '';
    
    return [
      codeString,
      status,
      enteredAt,
      device,
      qr?.freeText || '',
      qr?.comment || ''
    ];
  });
  
  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
  ].join('\r\n'); // Windows形式の改行コード
  
  // BOM付きUTF-8（Excelで正しく開くため）
  return '\uFEFF' + csvContent;
};

/**
 * CSVからフリー入力データをインポート
 * @param {string} csvText - CSV文字列
 * @param {string} ticketId - チケットID
 * @returns {Promise<{success: boolean, updatedCount: number, errors: Array}>}
 */
export const importQRCodeFreeTextFromCSV = async (csvText, ticketId) => {
  try {
    // 改行コードを統一（\r\n → \n）
    const normalizedText = csvText.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    const lines = normalizedText.trim().split('\n');
    
    console.log('[importQRCodeFreeTextFromCSV] Total lines:', lines.length);
    
    if (lines.length < 2) {
      throw new Error('CSVデータが空です');
    }

    // ヘッダー行を解析（カンマで分割、ダブルクォートを除去）
    const headerLine = lines[0];
    console.log('[importQRCodeFreeTextFromCSV] Header line:', headerLine);
    
    // CSVパース（簡易版）
    const parseCSVLine = (line) => {
      const result = [];
      let current = '';
      let inQuotes = false;
      
      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          result.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      result.push(current.trim());
      return result;
    };
    
    const headers = parseCSVLine(headerLine);
    console.log('[importQRCodeFreeTextFromCSV] Headers:', headers);
    
    // 全言語のヘッダー対応
    const codeHeaders = ['QRコード', 'QR Code', 'QR 코드', 'QR码', 'コード値'];
    const freeTextHeaders = ['フリー入力', 'Free Text', '자유 텍스트', '自由文本'];

    const codeIndex = headers.findIndex(h => codeHeaders.includes(h));
    const freeTextIndex = headers.findIndex(h => freeTextHeaders.includes(h));

    console.log('[importQRCodeFreeTextFromCSV] Column indexes:', { codeIndex, freeTextIndex });

    if (codeIndex === -1) {
      throw new Error('CSVにQRコード列が見つかりません');
    }
    if (freeTextIndex === -1) {
      throw new Error('CSVにフリー入力列が見つかりません');
    }

    // データ行を解析
    const updates = [];
    const errors = [];
    
    for (let i = 1; i < lines.length; i++) {
      try {
        if (!lines[i].trim()) continue; // 空行をスキップ
        
        const cells = parseCSVLine(lines[i]);
        const code = cells[codeIndex];
        const freeText = cells[freeTextIndex] || '';
        
        console.log(`[importQRCodeFreeTextFromCSV] Row ${i}:`, { code, freeText, cells });
        
        if (code) {
          updates.push({ code, freeText });
        }
      } catch (error) {
        errors.push(`行${i + 1}: ${error.message}`);
      }
    }

    console.log('[importQRCodeFreeTextFromCSV] Updates to process:', updates.length);
    console.log('[importQRCodeFreeTextFromCSV] First 5 updates:', updates.slice(0, 5));

    if (updates.length === 0) {
      throw new Error('インポートするデータがありません');
    }

    // RTDBのQRコードを更新
    const { rtdb } = initFirebaseTickets();
    let updatedCount = 0;

    // まず、qr_chunksを直接読み込む
    const chunksRef = ref(rtdb, `${getTicketsPath(ticketId)}/qr_chunks`);
    const chunksSnapshot = await get(chunksRef);
    
    if (!chunksSnapshot.exists()) {
      throw new Error('QRコードチャンクが見つかりません');
    }

    const chunks = chunksSnapshot.val();
    console.log('[importQRCodeFreeTextFromCSV] Found chunks:', Object.keys(chunks).length);

    // 各チャンクを走査してQRコードを更新
    const allCodesInDB = [];
    for (const [chunkId, chunk] of Object.entries(chunks)) {
      if (!chunk.codes || !Array.isArray(chunk.codes)) continue;
      
      let chunkUpdated = false;
      const updatedCodes = [...chunk.codes];
      
      for (let i = 0; i < updatedCodes.length; i++) {
        const qr = updatedCodes[i];
        if (!qr || !qr.code) continue;
        
        allCodesInDB.push(qr.code);
        
        // このQRコードに対応する更新を探す
        const updateData = updates.find(u => u.code === qr.code);
        if (updateData) {
          console.log('[importQRCodeFreeTextFromCSV] ✓ Updating:', qr.code, '→', updateData.freeText);
          updatedCodes[i] = {
            ...qr,
            freeText: updateData.freeText
          };
          chunkUpdated = true;
          updatedCount++;
        }
      }
      
      // チャンクを更新
      if (chunkUpdated) {
        const chunkRef = ref(rtdb, `${getTicketsPath(ticketId)}/qr_chunks/${chunkId}`);
        await update(chunkRef, { codes: updatedCodes });
      }
    }

    // デバッグ: マッチしなかったコードを表示
    if (updatedCount === 0) {
      console.warn('[importQRCodeFreeTextFromCSV] ⚠️ No matches found!');
      console.warn('[importQRCodeFreeTextFromCSV] Codes in CSV:', updates.map(u => u.code));
      console.warn('[importQRCodeFreeTextFromCSV] Codes in DB (first 10):', allCodesInDB.slice(0, 10));
      console.warn('[importQRCodeFreeTextFromCSV] Total codes in DB:', allCodesInDB.length);
    }

    console.log('[importQRCodeFreeTextFromCSV] Updated:', updatedCount, '/', updates.length);

    // インポート成功後、ローカルキャッシュをクリアして次回読み込み時に最新データを取得
    const cacheKey = `qr_codes_${ticketId}`;
    const syncKey = `qr_last_sync_${ticketId}`;
    localStorage.removeItem(cacheKey);
    localStorage.removeItem(syncKey);
    console.log('[importQRCodeFreeTextFromCSV] Cleared local cache for:', ticketId);

    return {
      success: true,
      updatedCount,
      totalCount: updates.length,
      errors
    };
  } catch (error) {
    console.error('[importQRCodeFreeTextFromCSV] Error:', error);
    throw error;
  }
};

/**
 * QRコードをPDF生成（PC側 - シール印刷向けフォーマット）
 * @param {Array} qrCodes - QRコード配列
 * @param {string} ticketName - チケット名
 * @param {string} layout - レイアウト: 'A4' (2x5=10), 'A4-4x5' (4x5=20), 'A4-4x6' (4x6=24)
 * @returns {Promise<void>} PDFをダウンロード
 */
export const generateQRCodePDF = async (qrCodes, ticketName, layout = 'A4-4x5') => {
  // PC側のみで使用
  if (typeof window === 'undefined') {
    throw new Error('This function must be called from PC/Web environment');
  }

  if (!Array.isArray(qrCodes) || qrCodes.length === 0) {
    throw new Error('PDF化するQRコードがありません');
  }
  
  try {
    const { jsPDF } = await import('jspdf');

    const layouts = {
      'A4': { cols: 2, rows: 5, gapX: 8, gapY: 10 },
      'A4-4x5': { cols: 4, rows: 5, gapX: 4, gapY: 6 },
      'A4-4x6': { cols: 4, rows: 6, gapX: 4, gapY: 4 }
    };

    const selectedLayout = layouts[layout] || layouts['A4-4x5'];
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
      compress: true
    });

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const marginX = 10;
    const marginBottom = 10;
    const headerHeight = 14;
    const gridStartY = 20;
    const itemsPerPage = selectedLayout.cols * selectedLayout.rows;
    const totalPages = Math.max(1, Math.ceil(qrCodes.length / itemsPerPage));

    const usableWidth = pageWidth - marginX * 2;
    const usableHeight = pageHeight - gridStartY - marginBottom;
    const cellWidth = (usableWidth - selectedLayout.gapX * (selectedLayout.cols - 1)) / selectedLayout.cols;
    const cellHeight = (usableHeight - selectedLayout.gapY * (selectedLayout.rows - 1)) / selectedLayout.rows;
    const labelHeight = 4;
    const qrDrawSize = Math.max(8, Math.min(cellWidth, cellHeight - labelHeight - 1));

    const rawTicketName = String(ticketName || 'ticket');
    const printableTicketTitle = /^[\x20-\x7E]+$/.test(rawTicketName) ? rawTicketName : 'Ticket';
    const safeTicketName = rawTicketName.replace(/[\\/:*?"<>|]/g, '_');
    const createdAtLabel = new Date().toLocaleString('en-GB');

    for (let page = 0; page < totalPages; page++) {
      if (page > 0) {
        pdf.addPage();
      }

      pdf.setFontSize(14);
      pdf.text(printableTicketTitle, marginX, 10);
      pdf.setFontSize(9);
      pdf.text(`Created: ${createdAtLabel}`, marginX, 14);
      pdf.text(`Total: ${qrCodes.length} (${layout})`, marginX, 17);
      pdf.text(`Page ${page + 1}/${totalPages}`, pageWidth - marginX, 17, { align: 'right' });

      const startIndex = page * itemsPerPage;
      const pageItems = qrCodes.slice(startIndex, startIndex + itemsPerPage);

      pageItems.forEach((qr, indexInPage) => {
        if (!qr?.imageData || typeof qr.imageData !== 'string') {
          return;
        }

        const row = Math.floor(indexInPage / selectedLayout.cols);
        const col = indexInPage % selectedLayout.cols;
        const cellX = marginX + col * (cellWidth + selectedLayout.gapX);
        const cellY = gridStartY + row * (cellHeight + selectedLayout.gapY);
        const imageX = cellX + (cellWidth - qrDrawSize) / 2;
        const imageY = cellY;

        pdf.addImage(qr.imageData, 'PNG', imageX, imageY, qrDrawSize, qrDrawSize, undefined, 'FAST');

        const absoluteIndex = startIndex + indexInPage + 1;
        pdf.setFontSize(8);
        pdf.text(String(absoluteIndex), cellX + cellWidth / 2, imageY + qrDrawSize + 3, { align: 'center' });
      });
    }

    const fileName = `qr_codes_${safeTicketName}_${new Date().toISOString().split('T')[0]}.pdf`;
    pdf.save(fileName);
    
  } catch (error) {
    console.warn('Generate PDF failed:', error);
    throw error;
  }
};

/**
 * ブラウザでダウンロード
 * @param {Blob|string} data - ダウンロードするデータ
 * @param {string} filename - ファイル名
 * @param {string} mimeType - MIMEタイプ
 */
export const downloadFile = async (data, filename, mimeType = 'application/octet-stream') => {
  // Electron環境: IPCでメインプロセスに保存を委譲（Blob URLはwill-downloadが発火しないため）
  if (window?.electron?.saveToDownloads) {
    try {
      const blob = typeof data === 'string'
        ? new Blob([data], { type: mimeType })
        : data;
      const arrayBuffer = await blob.arrayBuffer();
      const base64 = btoa(
        new Uint8Array(arrayBuffer).reduce((s, b) => s + String.fromCharCode(b), '')
      );
      const result = await window.electron.saveToDownloads({ filename, base64, mimeType });
      if (result?.ok) {
        // ダウンロード完了を通知（TicketPanelのonDownloadStatusと同等）
        if (typeof window.__onElectronFileSaved === 'function') {
          window.__onElectronFileSaved(result);
        }
        return result;
      }
      // 失敗時はフォールバック
      console.warn('[downloadFile] saveToDownloads failed, falling back:', result?.error);
    } catch (e) {
      console.warn('[downloadFile] Electron save failed, falling back:', e);
    }
  }

  // ブラウザ環境 or フォールバック
  const blob = typeof data === 'string' 
    ? new Blob([data], { type: mimeType })
    : data;
  
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// ========================================
// RevenueCat 課金処理
// ========================================

/**
 * Stripe/Apple/Google での支払い処理
 * （実装: PC側はStripe、モバイルはネイティブストア）
 * 
 * @param {string} plan - 'slot10'
 * @param {string} userId - ユーザーID
 * @param {number} quantity - 購入件数
 * @returns {Promise<Object>} { success, subscriptionId, expiresAt, error }
 */
export const processPurchase = async (plan, userId, quantity = 1) => {
  console.log('[processPurchase] Starting purchase:', { plan, userId, quantity });
  
  try {
    const { db } = initFirebaseTickets();
    
    // Stripe / RevenueCat API への支払い処理
    // 実装例：PC側は Stripe + RevenueCat Server フック
    //         結果を subscriptions に記録
    
    // Product Masterから商品情報を取得
    const config = await getProductFromMaster(plan);
    if (!config) {
      throw new Error('Invalid plan');
    }

    const safeQuantity = Math.max(1, Number(quantity || 1));
    const totalQRCodes = config.qrCount * safeQuantity;
    const totalPrice = config.price * safeQuantity;
    const now = new Date();
    const expiresAt = new Date(now.getTime() + config.durationDays * 24 * 60 * 60 * 1000);
    
    // サブスクリプション記録
    const subscriptionData = {
      userId: userId,
      plan: plan,
      unitQRCodes: config.qrCount,
      quantity: safeQuantity,
      maxQRCodes: totalQRCodes,
      purchasedAt: firestoreServerTimestamp(),
      expiresAt: expiresAt,
      status: 'active',
      renewalToken: '',  // RevenueCat token
      unitPrice: config.price,
      price: totalPrice,
      currency: 'JPY'
    };
    
    console.log('[processPurchase] Writing subscription to Firestore:', subscriptionData);
    const docRef = await addDoc(collection(db, 'subscriptions'), subscriptionData);
    console.log('[processPurchase] Subscription created successfully:', docRef.id);

    const localSubscriptions = readLocalSubscriptions();
    writeLocalSubscriptions([
      ...localSubscriptions,
      {
        id: docRef.id,
        ...subscriptionData,
        purchasedAt: new Date().toISOString(),
        expiresAt: normalizeDateLike(expiresAt)
      }
    ]);
    
    // 購入後にキャッシュをクリア（即座に反映させるため）
    clearSubscriptionCache(userId);
    console.log('[processPurchase] Cleared subscription cache for immediate update');
    
    return {
      success: true,
      subscriptionId: docRef.id,
      expiresAt: expiresAt,
      maxQRCodes: totalQRCodes,
      quantity: safeQuantity,
      price: totalPrice
    };
  } catch (error) {
    console.error('[processPurchase] Error:', error);
    console.error('[processPurchase] Error code:', error?.code);
    console.error('[processPurchase] Error message:', error?.message);
    
    if (isCommunicationError(error)) {
      const retryError = new Error('通信エラーが発生したため購入処理に失敗しました。再試行してください。');
      retryError.code = normalizeErrorCode(error) || 'network-request-failed';
      throw retryError;
    }

    console.warn('Purchase fallback to local:', error);

    // フォールバック: Product Masterから取得を試みる
    let fallback = await getProductFromMaster(plan);
    if (!fallback) {
      // それでも失敗した場合は、デフォルト値を使用
      fallback = {
        price: 100,
        qrCount: 10,
        durationDays: 30
      };
    }
    
    const safeQuantity = Math.max(1, Number(quantity || 1));
    const totalQRCodes = fallback.qrCount * safeQuantity;
    const totalPrice = fallback.price * safeQuantity;
    const now = new Date();
    const expiresAt = new Date(now.getTime() + fallback.durationDays * 24 * 60 * 60 * 1000);

    if (typeof localStorage !== 'undefined') {
      try {
        const key = 'localSubscriptions';
        const raw = localStorage.getItem(key);
        const current = raw ? JSON.parse(raw) : [];
        const safeCurrent = Array.isArray(current) ? current : [];
        safeCurrent.push({
          id: `local_sub_${Date.now()}`,
          userId,
          plan,
          unitQRCodes: fallback.qrCount,
          quantity: safeQuantity,
          maxQRCodes: totalQRCodes,
          expiresAt: expiresAt.toISOString(),
          status: 'active',
          unitPrice: fallback.price,
          price: totalPrice,
          currency: 'JPY'
        });
        localStorage.setItem(key, JSON.stringify(safeCurrent));
      } catch (storageError) {
        console.warn('Store local subscription failed:', storageError);
      }
    }

    return {
      success: true,
      subscriptionId: `local_sub_${Date.now()}`,
      expiresAt,
      maxQRCodes: totalQRCodes,
      quantity: safeQuantity,
      price: totalPrice,
      localMode: true
    };
  }
};

export const syncUserDataFromFirestoreToLocal = async (userId) => {
  if (!userId || typeof userId !== 'string' || !userId.trim()) {
    return { tickets: 0, qrCodes: 0, subscriptions: 0 };
  }

  const normalizedUserId = userId.trim();
  const { db, rtdb, auth } = initFirebaseTickets();
  if (!auth?.currentUser) {
    return { tickets: 0, qrCodes: 0, subscriptions: 0 };
  }

  let syncedTickets = 0;
  let syncedQRCodes = 0;
  let syncedSubscriptions = 0;

  // Tickets（Realtime Database）
  const ticketsRef = ref(rtdb, getTicketsPath());
  const ticketsQuery = rtdbQuery(ticketsRef, orderByChild('createdBy'), equalTo(normalizedUserId));
  const ticketsSnapshot = await get(ticketsQuery);
  
  const remoteTickets = ticketsSnapshot.exists() 
    ? snapshotToArray(ticketsSnapshot).map((ticket) => {
        const data = convertRtdbToFirestore(ticket);
        return {
          ...data,
          usedQRCodes: Number(data?.issuedCount ?? data?.usedQRCodes ?? 0),
          issuedCount: Number(data?.issuedCount ?? data?.usedQRCodes ?? 0),
          readCount: Number(data?.readCount ?? 0),
          createdAt: normalizeDateLike(data?.createdAt),
          expiresAt: normalizeDateLike(data?.expiresAt)
        };
      })
    : [];
  syncedTickets = remoteTickets.length;

  const existingLocalTickets = readLocalTickets();
  const keptTickets = existingLocalTickets.filter((ticket) => ticket?.createdBy !== normalizedUserId);
  writeLocalTickets([...keptTickets, ...remoteTickets]);

  for (const ticket of remoteTickets) {
    const chunksRef = ref(rtdb, getQRChunksPath(ticket.id));
    const chunkSnapshot = await get(chunksRef);
    let qrDocs = [];

    if (chunkSnapshot.exists()) {
      const chunksArray = snapshotToArray(chunkSnapshot);
      const sortedChunks = chunksArray
        .map((chunk) => convertRtdbToFirestore(chunk))
        .sort((a, b) => Number(a.startIndex || 0) - Number(b.startIndex || 0));

      qrDocs = sortedChunks.flatMap((chunkDoc) => {
        const codes = Array.isArray(chunkDoc.codes) ? chunkDoc.codes : [];
        return codes.map((code, indexInChunk) => ({
          id: createQRIndexDocId(code),
          code,
          chunkId: chunkDoc.chunkId || chunkDoc.id,
          offsetInChunk: indexInChunk,
          entryStatus: 'unused',
          enteredAt: null,
          enteredByDevice: null,
          createdAt: normalizeDateLike(chunkDoc.createdAt)
        }));
      });
    } else {
      const qrCodesRef = ref(rtdb, getQRCodesPath(ticket.id));
      const qrSnapshot = await get(qrCodesRef);
      
      if (qrSnapshot.exists()) {
        qrDocs = snapshotToArray(qrSnapshot).map((qr) => {
          const data = convertRtdbToFirestore(qr);
          return {
            ...data,
            createdAt: normalizeDateLike(data?.createdAt),
            enteredAt: normalizeDateLike(data?.enteredAt)
          };
        });
      }
    }

    writeLocalQRCodes(ticket.id, qrDocs);
    syncedQRCodes += qrDocs.length;
  }

  // Subscriptions（Firestore維持）
  const subscriptionsQuery = query(collection(db, 'subscriptions'), where('userId', '==', normalizedUserId));
  const subscriptionsSnapshot = await getDocs(subscriptionsQuery);
  const remoteSubscriptions = subscriptionsSnapshot.docs.map((docSnap) => ({
    id: docSnap.id,
    ...docSnap.data(),
    purchasedAt: normalizeDateLike(docSnap.data()?.purchasedAt),
    expiresAt: normalizeDateLike(docSnap.data()?.expiresAt)
  }));
  syncedSubscriptions = remoteSubscriptions.length;

  const existingSubscriptions = readLocalSubscriptions();
  const keptSubscriptions = existingSubscriptions.filter((item) => item?.userId !== normalizedUserId);
  writeLocalSubscriptions([...keptSubscriptions, ...remoteSubscriptions]);

  return {
    tickets: syncedTickets,
    qrCodes: syncedQRCodes,
    subscriptions: syncedSubscriptions
  };
};

/**
 * ユーザーのアクティブなサブスクリプション確認
 * @param {string} userId 
 * @returns {Promise<Object|null>} アクティブなサブスクリプションデータ
 */
export const getActiveSubscription = async (userId) => {
  try {
    if (!userId || typeof userId !== 'string' || !userId.trim()) {
      return null;
    }

    const normalizedUserId = userId.trim();
    
    // READ削減: まずローカルキャッシュから取得を試みる
    const getLocalActiveSubscription = () => {
      if (typeof localStorage === 'undefined') return null;
      try {
        const raw = localStorage.getItem('localSubscriptions');
        const parsed = raw ? JSON.parse(raw) : [];
        if (!Array.isArray(parsed)) return null;
        const nowLocal = new Date();
        const active = parsed
          .filter(item => item.userId === normalizedUserId && item.status === 'active')
          .map(item => {
            const expiresAtLocal = item.expiresAt?.toDate?.() || new Date(item.expiresAt);
            return { ...item, expiresAt: expiresAtLocal };
          })
          .filter(item => item.expiresAt > nowLocal)
          .sort((a, b) => b.expiresAt - a.expiresAt);

        if (active.length === 0) return null;
        return {
          ...active[0],
          daysRemaining: Math.ceil((active[0].expiresAt - nowLocal) / (1000 * 60 * 60 * 24))
        };
      } catch {
        return null;
      }
    };
    
    // ローカルキャッシュの最終同期時刻をチェック
    const lastSyncKey = `subscriptions_last_sync_${normalizedUserId}`;
    const lastSync = localStorage.getItem(lastSyncKey);
    const localActive = getLocalActiveSubscription();
    
    // ローカルキャッシュが新しい（1時間以内）場合はFirestoreアクセスをスキップ
    if (localActive && lastSync && Date.now() - new Date(lastSync).getTime() < SUBSCRIPTION_CACHE_TTL) {
      console.log('[getActiveSubscription] Using local cache (recent sync)');
      return localActive;
    }
    
    // Firestoreから取得（キャッシュが古い、または存在しない場合のみ）
    const { db } = initFirebaseTickets();
    const q = query(
      collection(db, 'subscriptions'),
      where('userId', '==', normalizedUserId),
      where('status', '==', 'active')
    );
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      // Firestoreにない場合はローカルキャッシュを返す
      return localActive;
    }
    
    const now = new Date();
    for (const doc of snapshot.docs) {
      const data = doc.data();
      const expiresAt = data.expiresAt.toDate?.() || new Date(data.expiresAt);
      
      // 有効期限内のサブスクリプションを返す
      if (expiresAt > now) {
        const result = {
          id: doc.id,
          ...data,
          expiresAt: expiresAt,
          daysRemaining: Math.ceil((expiresAt - now) / (1000 * 60 * 60 * 24))
        };
        
        // ローカルキャッシュを更新
        const allSubscriptions = readLocalSubscriptions();
        const updated = allSubscriptions.filter(s => s.id !== doc.id);
        updated.push(result);
        writeLocalSubscriptions(updated);
        localStorage.setItem(lastSyncKey, new Date().toISOString());
        
        return result;
      } else {
        // 期限切れ → 無効化
        await updateDoc(doc.ref, { status: 'expired' });
      }
    }
    
    return localActive;
  } catch (error) {
    const code = error?.code || '';
    if (code !== 'permission-denied' && code !== 'unauthenticated') {
      console.warn('Get subscription skipped:', error);
    }
    if (typeof localStorage !== 'undefined') {
      try {
        const raw = localStorage.getItem('localSubscriptions');
        const parsed = raw ? JSON.parse(raw) : [];
        if (!Array.isArray(parsed)) return null;
        const now = new Date();
        const active = parsed
          .filter(item => item.userId === userId.trim() && item.status === 'active')
          .map(item => ({ ...item, expiresAt: new Date(item.expiresAt) }))
          .find(item => item.expiresAt > now);
        if (!active) return null;
        return {
          ...active,
          daysRemaining: Math.ceil((active.expiresAt - now) / (1000 * 60 * 60 * 24))
        };
      } catch {
        return null;
      }
    }
    return null;
  }
};

/**
 * 有効期限チェック & クリーンアップ
 * （定期実行: 毎日 or CloudFunction で）
 */
export const checkAndCleanupExpiredSubscriptions = async () => {
  try {
    const { db } = initFirebaseTickets();
    const now = new Date();
    
    // 期限切れサブスクリプション取得
    const q = query(
      collection(db, 'subscriptions'),
      where('status', '==', 'active')
    );
    const snapshot = await getDocs(q);
    
    const batch = writeBatch(db);
    let updatedCount = 0;
    
    snapshot.docs.forEach(doc => {
      const data = doc.data();
      const expiresAt = data.expiresAt.toDate?.() || new Date(data.expiresAt);
      
      if (expiresAt <= now) {
        batch.update(doc.ref, { status: 'expired' });
        updatedCount++;
      }
    });
    
    if (updatedCount > 0) {
      await batch.commit();
      console.log(`${updatedCount} expired subscriptions updated`);
    }
    
    return updatedCount;
  } catch (error) {
    console.error('Cleanup subscriptions failed:', error);
    throw error;
  }
};

/**
 * チケットの利用可能QR数確認
 * @param {string} ticketId 
 * @param {string} userId 
 * @returns {Promise<Object>} { available: boolean, remaining: number, message: string }
 */
export const checkTicketQRAvailability = async (ticketId, userId) => {
  try {
    const { db } = initFirebaseTickets();
    
    // チケット取得
    const ticket = await getTicket(ticketId);
    if (!ticket) {
      return { available: false, remaining: 0, message: 'チケットが見つかりません' };
    }
    
    // アクティブなサブスクリプション確認
    const subscription = await getActiveSubscription(userId);
    if (!subscription) {
      return { 
        available: false, 
        remaining: 0, 
        message: 'アクティブなプランがありません' 
      };
    }
    
    // 有効期限確認
    const now = new Date();
    if (subscription.expiresAt <= now) {
      return { 
        available: false, 
        remaining: 0, 
        message: 'サブスクリプションの有効期限切れです' 
      };
    }
    
    // 使用済みQR確認
    const remaining = subscription.maxQRCodes - ticket.usedQRCodes;
    if (remaining <= 0) {
      return { 
        available: false, 
        remaining: 0, 
        message: 'QRコード上限に達しました' 
      };
    }
    
    return {
      available: true,
      remaining: remaining,
      message: `残り ${remaining} のQRコードを生成可能`
    };
  } catch (error) {
    console.error('Check availability failed:', error);
    return { 
      available: false, 
      remaining: 0, 
      message: error.message 
    };
  }
};

/**
 * プラン追加購入（既存期限を延長）
 * @param {string} userId 
 * @param {string} plan 
 * @returns {Promise<Object>} 更新結果
 */
export const addPlanAddition = async (userId, plan) => {
  try {
    const { db } = initFirebaseTickets();
    
    // 最新のサブスクリプション取得
    const activeSubscription = await getActiveSubscription(userId);
    
    const planConfig = {
      basic: { qrCount: 1000, durationDays: 30, price: 1000 },
      standard: { qrCount: 10000, durationDays: 30, price: 10000 }
    };
    
    if (!planConfig[plan]) {
      throw new Error('Invalid plan');
    }
    
    const config = planConfig[plan];
    let newExpiresAt;
    
    if (activeSubscription) {
      // 既存期限を延長
      newExpiresAt = new Date(
        activeSubscription.expiresAt.getTime() + config.durationDays * 24 * 60 * 60 * 1000
      );
      
      // 既存サブスクを更新
      await updateDoc(doc(db, 'subscriptions', activeSubscription.id), {
        expiresAt: newExpiresAt,
        maxQRCodes: increment(config.qrCount)
      });
    } else {
      // 新規作成
      const now = new Date();
      newExpiresAt = new Date(now.getTime() + config.durationDays * 24 * 60 * 60 * 1000);
      
      await addDoc(collection(db, 'subscriptions'), {
        userId: userId,
        plan: plan,
        maxQRCodes: config.qrCount,
        purchasedAt: firestoreServerTimestamp(),
        expiresAt: newExpiresAt,
        status: 'active'
      });
    }
    
    return {
      success: true,
      newExpiresAt: newExpiresAt,
      message: `${plan === 'basic' ? '1000' : '10000'} のQRコードを追加しました`
    };
  } catch (error) {
    console.error('Add plan failed:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * ========================================
 * メンテナンスモード管理
 * ========================================
 */

/**
 * メンテナンスステータスを取得
 * @returns {Promise<{status: number, excludeUsers: string[], messages: object}>}
 */
export const getMaintenanceStatus = async () => {
  try {
    const { rtdb, realtimeDatabasePath } = getFirebaseTickets();
    const maintenanceRef = ref(rtdb, `${realtimeDatabasePath}/maintenance`);
    const snapshot = await get(maintenanceRef);
    
    if (!snapshot.exists()) {
      return { 
        status: 0, 
        excludeUsers: [],
        messages: {
          en: 'System is under maintenance. Please wait.',
          ja: 'システムメンテナンス中です。しばらくお待ちください。',
          zh: '系统维护中。请稍候。',
          ko: '시스템 점검 중입니다. 잠시만 기다려 주세요.'
        }
      };
    }
    
    const data = snapshot.val();
    
    // exclude_usersから元の値を取得（サニタイズされたキーから値を復元）
    const excludeUsers = data.exclude_users 
      ? Object.values(data.exclude_users) 
      : [];
    
    const messages = data.messages || {
      en: 'System is under maintenance. Please wait.',
      ja: 'システムメンテナンス中です。しばらくお待ちください。',
      zh: '系统维护中。请稍候。',
      ko: '시스템 점검 중입니다. 잠시만 기다려 주세요.'
    };
    
    console.log('[getMaintenanceStatus] Realtime Databaseから取得:', data);
    console.log('[getMaintenanceStatus] excludeUsers:', excludeUsers);
    console.log('[getMaintenanceStatus] messages:', messages);
    
    return {
      status: data.status || 0,
      excludeUsers,
      messages
    };
  } catch (error) {
    console.error('[getMaintenanceStatus] Error:', error);
    return { 
      status: 0, 
      excludeUsers: [],
      messages: {
        en: 'System is under maintenance. Please wait.',
        ja: 'システムメンテナンス中です。しばらくお待ちください。',
        zh: '系统维护中。请稍候。',
        ko: '시스템 点検 中입니다. 잠시만 기다려 주세요.'
      }
    };
  }
};

/**
 * ユーザーがアクセス可能かチェック
 * @param {string} firebaseUid - Firebase UID
 * @param {string} email - ユーザーのメールアドレス
 * @param {string} canonicalUserId - Canonical User ID (オプション)
 * @param {string} deviceUniqueId - Device Unique ID (オプション)
 * @returns {Promise<{allowed: boolean, reason: string, messages: object}>}
 */
export const checkUserAccess = async (firebaseUid, email, canonicalUserId = null, deviceUniqueId = null) => {
  try {
    const { status, excludeUsers, messages } = await getMaintenanceStatus();
    
    console.log('[checkUserAccess] status:', status);
    console.log('[checkUserAccess] excludeUsers:', excludeUsers);
    console.log('[checkUserAccess] messages:', messages);
    
    // 通常モード
    if (status === 0) {
      return { allowed: true, reason: 'normal' };
    }
    
    // 全員メンテナンス
    if (status === 1) {
      return { 
        allowed: false, 
        reason: 'maintenance_all',
        messages: messages
      };
    }
    
    // 除外ユーザーあり
    if (status === 2) {
      const normalizedEmail = email ? email.toLowerCase().trim() : '';
      
      // 複数の識別子でチェック
      const isExcluded = excludeUsers.some(excludedUser => {
        const normalizedExcluded = excludedUser.toLowerCase().trim();
        
        // Firebase UID
        if (firebaseUid && normalizedExcluded === firebaseUid.toLowerCase()) {
          return true;
        }
        
        // メールアドレス
        if (normalizedEmail && normalizedExcluded === normalizedEmail) {
          return true;
        }
        
        // Canonical User ID
        if (canonicalUserId && normalizedExcluded === canonicalUserId.toLowerCase()) {
          return true;
        }
        
        // Device Unique ID
        if (deviceUniqueId && normalizedExcluded === deviceUniqueId.toLowerCase()) {
          return true;
        }
        
        return false;
      });
      
      console.log('[checkUserAccess] firebaseUid:', firebaseUid);
      console.log('[checkUserAccess] normalizedEmail:', normalizedEmail);
      console.log('[checkUserAccess] canonicalUserId:', canonicalUserId);
      console.log('[checkUserAccess] deviceUniqueId:', deviceUniqueId);
      console.log('[checkUserAccess] isExcluded:', isExcluded);
      
      if (isExcluded) {
        return { allowed: true, reason: 'excluded_user' };
      } else {
        return { 
          allowed: false, 
          reason: 'maintenance_excluded',
          messages: messages
        };
      }
    }
    
    return { allowed: true, reason: 'unknown' };
  } catch (error) {
    console.error('[checkUserAccess] Error:', error);
    return { allowed: false, reason: 'error' };
  }
};

/**
 * メンテナンスステータスをリアルタイム監視
 * @param {Function} callback - (status, excludeUsers) => void
 * @returns {Function} unsubscribe function
 */
export const subscribeToMaintenanceStatus = (callback) => {
  try {
    const { rtdb, realtimeDatabasePath } = getFirebaseTickets();
    const maintenanceRef = ref(rtdb, `${realtimeDatabasePath}/maintenance`);
    
    const unsubscribe = onValue(maintenanceRef, (snapshot) => {
      if (!snapshot.exists()) {
        callback(0, []);
        return;
      }
      
      const data = snapshot.val();
      // exclude_usersから元の値を取得（サニタイズされたキーから値を復元）
      const excludeUsers = data.exclude_users 
        ? Object.values(data.exclude_users) 
        : [];
      callback(data.status || 0, excludeUsers);
    });
    
    return unsubscribe;
  } catch (error) {
    console.error('[subscribeToMaintenanceStatus] Error:', error);
    return () => {};
  }
};

/**
 * メンテナンスモードのメッセージを取得
 * i18n.jsを使用
 * @param {string} reason - checkUserAccessから返されたreason
 * @param {string} language - 言語コード ('ja' or 'en')
 * @returns {string}
 */
export const getMaintenanceMessage = (reason, language = 'en') => {
  const { getTranslation } = require('./i18n');
  
  const messageKeys = {
    maintenance_all: 'maintenance.all',
    maintenance_excluded: 'maintenance.excluded',
    error: 'maintenance.error'
  };
  
  const key = messageKeys[reason] || messageKeys.error;
  return getTranslation(key, language);
};
